/**
 * ShowModal - Cribbage Show Modal & Scoring Presentation System
 * Encapsulates the hand & crib scoring breakdown modal, pegging log details,
 * mini card HTML renderers, meter display, and big-hand fanfares.
 */

import { CelebrationFX } from './CelebrationFX.js';
import { AchievementManager } from '../achievements/AchievementManager.js';
import { HandScoreTracker } from '../stats/HandScoreTracker.js';
import { HandOfGloryCard } from './HandOfGloryCard.js';
import { CardAssassinOverlay } from './CardAssassinOverlay.js';
import { GameReplayManager } from '../game/GameReplayManager.js';
import { CribbageScorer } from '../scoring/CribbageScorer.js';
import { GameEngine } from '../game/GameEngine.js';
import { TraditionalCard } from './TraditionalCard.js';

export class ShowModal {
  static isBonusMessage(text) {
    if (!text) return false;
    const s = String(text);
    return /sign|plus 5|plus 10|minus 5|minus 10|wheel|dice|undo|selected|chose sign|all red|all black|all odd|all even|all diff|suicide king|king of hearts|queen of spades|ace of spades|spades cut|no spade|spades|guess starter|starter guess|starter guessed|full house|straight flush|royal flush|straight|stolen crib|crib stolen|thirty-one for five|31.*for 5|nineteen for two|19.*for 2|thirty-one|nineteen|detour|round about|jail|bar|timeout|passing lane|stop sign|partner|flush in play|flush of|bonus/i.test(s);
  }

  static getCardLabel(c) {
    if (typeof window !== 'undefined' && typeof window.cardLabel === 'function') {
      return window.cardLabel(c);
    }
    if (!c) return '';
    const r = (c.playAs && c.playAs.r) || c.r || c.rank || '';
    const s = (c.playAs && c.playAs.s) || c.s || c.suit || '';
    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
    return `${r}${suitSyms[s] || s}`;
  }

  static miniCardHtml(c, extra = '') {
    if (!c) return '';
    const s = c.s || c.suit || '';
    const red = s === 'H' || s === 'D';
    const label = ShowModal.getCardLabel(c);
  }

  static bindModalEvents(root = (typeof document !== 'undefined' ? (document.getElementById('modalContent') || document.getElementById('modal')) : null)) {
    if (!root || root.__showModalEventsBound) return;
    root.__showModalEventsBound = true;
    root.addEventListener('click', (e) => {
      const gloryBtn = e.target.closest('[data-action="hand-of-glory"]');
      if (gloryBtn) {
        const seat = gloryBtn.dataset.seat || gloryBtn.getAttribute('data-seat');
        if (typeof window !== 'undefined' && typeof window.showHandOfGloryModal === 'function') {
          window.showHandOfGloryModal(seat);
        }
        return;
      }
      const pegBtn = e.target.closest('[data-action="show-peg-points"]');
      if (pegBtn) {
        if (typeof window !== 'undefined' && typeof window.showPegPoints === 'function') {
          window.showPegPoints();
        } else {
          ShowModal.showPegPoints();
        }
        return;
      }
      const handBtn = e.target.closest('[data-action="show-hand-points"]');
      if (handBtn) {
        const key = handBtn.dataset.key || handBtn.getAttribute('data-key');
        if (typeof window !== 'undefined' && typeof window.showHandPoints === 'function') {
          window.showHandPoints(key);
        } else {
          ShowModal.showHandPoints(key);
        }
        return;
      }
      const chipBtn = e.target.closest('[data-action="select-stolen-chip"]');
      if (chipBtn) {
        const amt = parseInt(chipBtn.dataset.amount, 10);
        if (!isNaN(amt)) {
          ShowModal.selectStolenChip(amt);
        }
        return;
      }
    });
  }

  /**
   * Retrieves the kept hand cards for a specific seat/player across all phase structures.
   * Handles 2-player ('pone', 'dealer') and multi-player ('p1'..'p8') state formats.
   * @param {string} trackOrSeat
   * @param {Object} state
   * @param {Object} [context]
   * @returns {Array} Array of Card objects
   */
  static getPlayerHandCards(trackOrSeat, state, context = {}) {
    if (!trackOrSeat || !state) return [];
    let sId = String(trackOrSeat);
    if (sId === 'you') sId = 'p1';
    if (sId === 'cpu') sId = 'p2';

    // 1. Search in state.lastShow entries (matches 'who' property for 2-player 'pone'/'dealer' and direct keys)
    if (state.lastShow && typeof state.lastShow === 'object') {
      if (Array.isArray(state.lastShow[sId]?.cards) && state.lastShow[sId].cards.length) {
        return state.lastShow[sId].cards;
      }
      for (const k of Object.keys(state.lastShow)) {
        const item = state.lastShow[k];
        if (item && item.who) {
          let itemWho = String(item.who);
          if (itemWho === 'you') itemWho = 'p1';
          if (itemWho === 'cpu') itemWho = 'p2';
          if (itemWho === sId && Array.isArray(item.cards) && item.cards.length) {
            return item.cards;
          }
        }
      }
    }

    // 2. Check getSeatKept from context, window, or GameState
    const getSeatKeptFn = context.getSeatKept || (typeof window !== 'undefined' ? (window.getSeatKept || window.GameState?.getSeatKept) : null);
    if (typeof getSeatKeptFn === 'function') {
      try {
        const kept = getSeatKeptFn(sId, state) || getSeatKeptFn(sId);
        if (Array.isArray(kept) && kept.length) return kept;
      } catch (_) {}
    }

    // 3. Check seatKept, kept, youKept, cpuKept direct references on state
    if (Array.isArray(state.seatKept?.[sId]) && state.seatKept[sId].length) return state.seatKept[sId];
    if (Array.isArray(state.kept?.[sId]) && state.kept[sId].length) return state.kept[sId];
    if (sId === 'p1') {
      if (Array.isArray(state.youKept) && state.youKept.length) return state.youKept;
      if (Array.isArray(state.kept?.p1) && state.kept.p1.length) return state.kept.p1;
    }
    if (sId === 'p2') {
      if (Array.isArray(state.cpuKept) && state.cpuKept.length) return state.cpuKept;
      if (Array.isArray(state.kept?.p2) && state.kept.p2.length) return state.kept.p2;
    }

    // 4. Fallback to hands/seatHands
    if (Array.isArray(state.seatHands?.[sId]) && state.seatHands[sId].length) return state.seatHands[sId];
    if (Array.isArray(state.hands?.[sId]) && state.hands[sId].length) return state.hands[sId];
    if (sId === 'p1' && Array.isArray(state.youHand) && state.youHand.length) return state.youHand;
    if (sId === 'p2' && Array.isArray(state.cpuHand) && state.cpuHand.length) return state.cpuHand;

    return [];
  }

  static showPegPoints(options = {}) {
    if (typeof document === 'undefined') return;
    const box = document.getElementById('handDetail');
    if (!box) return;
    if (box.dataset.open === 'peg') {
      box.innerHTML = '';
      box.dataset.open = '';
      return;
    }

    const state = options.state || (typeof window !== 'undefined' ? window.state : {}) || {};
    const log = state.pegLog || [];
    const count = typeof window !== 'undefined' && typeof window.getPlayerCount === 'function'
      ? window.getPlayerCount()
      : 2;
    const isPart = typeof window !== 'undefined' && typeof window.isPartnership === 'function'
      ? window.isPartnership()
      : false;
    const pName = (typeof window !== 'undefined' && typeof window.pName === 'function')
      ? window.pName
      : (w => w);

    let pegTotalsStr = '';
    if (isPart) {
      pegTotalsStr = `Team 1 ${(state.handPeg && state.handPeg.team1) || 0} · Team 2 ${(state.handPeg && state.handPeg.team2) || 0}`;
    } else if (count > 2 && typeof window !== 'undefined' && typeof window.getActiveSeats === 'function') {
      pegTotalsStr = window.getActiveSeats().map(s => `${s.name} ${(state.handPeg && state.handPeg[s.id]) || 0}`).join(' · ');
    } else {
      pegTotalsStr = `${pName('you')} ${(state.handPeg && state.handPeg.you) || 0} · ${pName('cpu')} ${(state.handPeg && state.handPeg.cpu) || 0}`;
    }

    const rows = log.length
      ? '<ul class="pt-lines">' + log.map(e => {
          const isB = ShowModal.isBonusMessage(e.why);
          return `<li class="${isB ? 'pt-line-bonus' : ''}">${isB ? '<span class="pt-bonus-pill">BONUS</span> ' : ''}<strong>${pName(e.who)} +${e.n}</strong> ${e.why}` +
            (e.card ? ' — ' + ShowModal.getCardLabel(e.card) : '') +
            (e.count ? ' <span style="opacity:.7">(count ' + e.count + ')</span>' : '') +
            '</li>';
        }).join('') + '</ul>'
      : '<p>No pegging points this hand.</p>';

    box.dataset.open = 'peg';
    box.innerHTML = `<div class="hand-detail">
      <strong>Pegging</strong> — ${pegTotalsStr}
      ${rows}
    </div>`;
  }

  static showHandPoints(key, options = {}) {
    if (typeof document === 'undefined') return;
    const box = document.getElementById('handDetail');
    const state = options.state || (typeof window !== 'undefined' ? window.state : {}) || {};
    if (!box || !state.lastShow || !state.lastShow[key]) return;
    if (box.dataset.open === key) {
      box.innerHTML = '';
      box.dataset.open = '';
      return;
    }

    const h = state.lastShow[key];
    const cards = h.cards || [];
    const lines = (h.score && h.score.lines) || [];
    const lineHtml = lines.length
      ? '<ul class="pt-lines">' + lines.map(l => {
          const isB = l.isBonus || ShowModal.isBonusMessage(l.why);
          return `<li class="${isB ? 'pt-line-bonus' : ''}">${isB ? '<span class="pt-bonus-pill">BONUS</span> ' : ''}<strong>+${l.n}</strong> ${l.why}${l.cards && l.cards.length ? ' — ' + l.cards.map(ShowModal.getCardLabel).join(' ') : ''}</li>`;
        }).join('') + '</ul>'
      : '<p>No scoring combinations.</p>';

    box.dataset.open = key;
    const starters = (h.starters && h.starters.length) ? h.starters : (h.starter ? [h.starter] : []);
    const startersHtml = starters.map(s => ShowModal.miniCardHtml(s, 'cut')).join('');
    box.innerHTML = `<div class="hand-detail">
      <strong>${h.title}</strong> — ${h.signed} point${Math.abs(h.signed) === 1 ? '' : 's'}
      <div class="mini-row">${cards.map(c => ShowModal.miniCardHtml(c)).join('')}${startersHtml}</div>
      <div style="font-size:.8rem;opacity:.8">Starter${starters.length > 1 ? 's' : ''} outlined in gold</div>
      ${lineHtml}
      <div style="margin-top: 10px; text-align: center;">
        <button type="button" class="btn-glory-pill" data-action="hand-of-glory" data-seat="${key}" title="Generate shareable Hand of Glory graphic card">
          <span>🏆</span> <span>Hand of Glory Card</span>
        </button>
      </div>
    </div>`;
    ShowModal.bindModalEvents();
  }

  static cardKey(c) {
    if (!c) return '';
    if (c.id) return c.id;
    const r = c.r || c.rank || '';
    const s = c.s || c.suit || '';
    return r && s ? `${r}-${s}` : '';
  }

  /**
   * Renders the active hand or crib counter modal dialog with cards and live show meter.
   * @param {Object} pack
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static renderShowCountModal(pack, actIdx = 0, context = {}) {
    if (!pack) return;
    const showModal = context.showModal || (typeof window !== 'undefined' ? window.showModal : null);
    if (typeof showModal !== 'function') return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const key = (state && state.showQueue && state.showQueue[actIdx]) || state?.showAct || '';
    const isCrib = key === 'crib' || pack.who === 'crib';

    const cards = (pack.cards && pack.cards.length) ? pack.cards : [];
    const starters = (pack.starters && pack.starters.length) ? pack.starters : (pack.starter ? [pack.starter] : []);

    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
    const rankNames = { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' };

    const getCid = (c) => {
      const fn = context.cardKey || (typeof window !== 'undefined' ? window.cardKey : null) || ShowModal.cardKey;
      return fn(c) || ShowModal.cardKey(c);
    };

    const renderCard = (c, isCut = false) => {
      if (!c) return '';
      const suit = c.suit || c.s || 'S';
      const isRed = suit === 'H' || suit === 'D';
      const color = isRed ? '#dc2626 !important' : '#111827 !important';
      const r = c.rankName || c.r || rankNames[c.rank] || c.rank || '★';
      const s = suitSyms[suit] || suit || '';
      const cid = getCid(c);
      return `
        <div class="playing-card show-count-card${isCut ? ' cut-card' : ''}" data-cid="${cid}"
             style="position: relative; width: 58px; height: 84px; box-sizing: border-box; background: #ffffff !important; border: 2px solid ${isCut ? '#eab308' : '#cbd5e1'}; border-radius: 7px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.35); text-align: left; transition: transform 0.22s ease, box-shadow 0.22s ease, outline 0.22s ease;">
          ${TraditionalCard.renderHtml(c, { isCut })}
        </div>
      `;
    };

    const cardsHtml = cards.map(c => renderCard(c, false)).join('');
    const startersHtml = starters.map(s => renderCard(s, true)).join('');

    const html = `
      <div class="show-counter-modal" id="showCountModal">
        <div class="show-count-badge">
          <span class="show-count-icon">${isCrib ? '🧺' : '🎴'}</span>
          <span>${isCrib ? 'CRIB COUNT' : 'HAND COUNT'}</span>
        </div>
        <h2 class="show-count-title">${pack.title || (isCrib ? 'The Crib' : 'Hand Count')}</h2>

        <div class="show-count-cards-wrap">
          <div class="show-count-cards-group">
            ${cardsHtml}
          </div>
          ${startersHtml ? `
            <div class="show-count-divider">+</div>
            <div class="show-count-cards-group cut-group">
              ${startersHtml}
            </div>
          ` : ''}
        </div>
        <div class="show-count-cut-note">Starter${starters.length > 1 ? 's' : ''} outlined in gold</div>

        <div class="show-meter" id="showMeter">
          <div class="show-act" id="showActLabel">${pack.title || 'Show'}</div>
          <div class="show-run"><span id="showRunVal">${state?.showRun || 0}</span></div>
          <div class="show-line" id="showLine">${state?.showMeter?.line || 'Flipping…'}</div>
        </div>

        <div class="show-combo-feed" id="showComboFeed"></div>
      </div>
    `;

    showModal(html, false);
  }

  static setShowMeter(act, total, line, isBonus = false, context = {}) {
    if (typeof document === 'undefined') return;
    let box = document.getElementById('showMeter');
    if (!box) {
      const state = context.state || (typeof window !== 'undefined' ? window.state : null);
      if (state && state.phase === 'show' && state.lastShow && state.showAct && state.lastShow[state.showAct]) {
        const pack = state.lastShow[state.showAct];
        ShowModal.renderShowCountModal(pack, 0, context);
        box = document.getElementById('showMeter');
      }
    }
    if (!box) return;
    box.classList.toggle('hidden', !act);
    box.classList.toggle('bonus-active', !!isBonus);
    const lab = document.getElementById('showActLabel');
    const val = document.getElementById('showRunVal');
    const ln = document.getElementById('showLine');
    if (lab) lab.textContent = act || '';
    if (val) val.textContent = String(total || 0);
    if (ln) {
      ln.innerHTML = isBonus ? `<span class="show-line-bonus">${line}</span>` : (line || '');
    }
  }

  static famousFanfare(n, options = {}) {
    if (typeof window === 'undefined') return;
    const sfx = options.sfx || window.sfx;
    const fancyCall = options.fancyCall || window.fancyCall;
    if (n >= 20) {
      CelebrationFX.triggerCelebration(n, { sfx });
    }
    if (n >= 24 && typeof window !== 'undefined' && window._currentRenderer3D) {
      window._currentRenderer3D.triggerActionCam({ type: 'big_hand', score: n });
    }
    if (n === 29) {
      AchievementManager.unlock('holy_grail', { sfx });
      AchievementManager.unlock('twenty_pointer', { sfx });
    } else {
      if (n >= 100) AchievementManager.unlock('crazy_mythic', { sfx });
      if (n >= 75) AchievementManager.unlock('crazy_godlike', { sfx });
      if (n >= 60) AchievementManager.unlock('crazy_legendary', { sfx });
      if (n >= 50) AchievementManager.unlock('crazy_awesome', { sfx });
      if (n >= 40) AchievementManager.unlock('crazy_amazing', { sfx });
      if (n >= 35) AchievementManager.unlock('century_club', { sfx });
      if (n >= 30) AchievementManager.unlock('crazy_good', { sfx });
      if (n >= 20) AchievementManager.unlock('twenty_pointer', { sfx });
    }
    if (typeof fancyCall === 'function') {
      if (n === 29) fancyCall('Twenty-nine!', 'famous', 2200);
      else if (n === 28) fancyCall('Twenty-eight!', 'famous', 2000);
      else if (n === 24) fancyCall('Twenty-four!', 'famous', 1800);
      else if (n >= 20) fancyCall(n + ' Points!', 'famous', 1600);
    }
  }

  static open(pegOnly = false, options = {}) {
    const state = options.state || (typeof window !== 'undefined' ? window.state : {}) || {};
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : {}) || {};
    const pName = (typeof window !== 'undefined' && typeof window.pName === 'function') ? window.pName : (w => w);

    if (state && state.over) {
      state.showModal = null;
      state.showMeter = null;
      const checkGameOverFn = options.checkGameOver || (typeof window !== 'undefined' ? window.checkGameOver : null);
      if (typeof checkGameOverFn === 'function') checkGameOverFn(options);
      return;
    }

    if (typeof document === 'undefined') return;

    if (state && !state.handRecorded) {
      state.handRecorded = true;
      state.handsCompleted = (state.handsCompleted || 0) + 1;
    }

    if (typeof window !== 'undefined' && window.gameHands && typeof window.saveHand === 'function') {
      window.saveHand(state);
    }
    HandScoreTracker.recordHand(state, settings, { pName });

    state.showModal = { open: true, pegOnly: !!pegOnly };
    const ls = state.lastShow || {};
    const count = typeof window !== 'undefined' && typeof window.getPlayerCount === 'function' ? window.getPlayerCount() : 2;
    const isPart = typeof window !== 'undefined' && typeof window.isPartnership === 'function' ? window.isPartnership() : false;
    const isGuest = typeof window !== 'undefined' && typeof window.isOnline === 'function' && window.isOnline() && settings.netRole === 'guest';
    const actionBtn = isGuest
      ? `<div class="btn-gold" id="btnGuestWait" style="opacity:.85;cursor:default;text-align:center;padding:12px 20px;letter-spacing:.05em;">Waiting for Host to start next hand…</div>`
      : `<button class="btn-gold" id="btnNextHand">Next hand</button>`;

    const formatNotes = (notes) => {
      if (!notes || !notes.length) return '0';
      return notes.map(n => ShowModal.isBonusMessage(n) ? `<span class="note-bonus">${n}</span>` : n).join(' · ');
    };

    let breakdownHtml = '';
    let scoresHtml = '';
    const animIds = ['tickPeg'];

    if (count === 2) {
      const youPeg = (state.handPeg && state.handPeg.you) || 0;
      const cpuPeg = (state.handPeg && state.handPeg.cpu) || 0;
      const poneScore = (ls.pone && ls.pone.score) || { total: 0, notes: [] };
      const dealScore = (ls.dealer && ls.dealer.score) || { total: 0, notes: [] };
      const cribSigned = ls.crib ? ls.crib.signed : 0;
      const cribNotes = (ls.crib && ls.crib.score && ls.crib.score.notes) || [];
      const pone = state.dealer === 'you' ? 'cpu' : 'you';
      const poneBlocked = ls.pone && ls.pone.blockedByZone;
      const dealBlocked = ls.dealer && ls.dealer.blockedByZone;
      const cribBlocked = ls.crib && ls.crib.blockedByZone;
      const poneReason = (ls.pone && ls.pone.blockedReason) || 'Peg Zone';
      const dealReason = (ls.dealer && ls.dealer.blockedReason) || 'Peg Zone';
      const cribReason = (ls.crib && ls.crib.blockedReason) || 'Peg Zone';
      const zoneColor = (r) => (r === 'Bunker' ? '#eab308' : (r === 'Last 5 Peg Out' ? '#f43f5e' : '#f97316'));
      const poneCounted = poneBlocked ? 0 : poneScore.total;
      const dealCounted = dealBlocked ? 0 : dealScore.total;
      const cribCounted = cribBlocked ? 0 : cribSigned;
      const cribRecipient = (ls.crib && ls.crib.who) || state.dealer;
      const isYouCrib = (cribRecipient === 'you' || cribRecipient === 'p1');
      const youShow = pegOnly ? 0 : (pone === 'you' ? poneCounted : dealCounted) + (isYouCrib ? cribCounted : 0);
      const cpuShow = pegOnly ? 0 : (pone === 'cpu' ? poneCounted : dealCounted) + (!isYouCrib ? cribCounted : 0);

      const stolenInfo = state.stolenCrib;
      let stolenTag = '';
      if (stolenInfo) {
        if (stolenInfo.success) {
          stolenTag = ` <span class="note-bonus" style="font-weight:700;">[STOLEN by ${pName(stolenInfo.pone)}: guessed ${stolenInfo.guess} pts!]</span>`;
        } else {
          stolenTag = ` <span style="color:#94a3b8;font-size:0.85em;">[${pName(stolenInfo.pone)} guessed ${stolenInfo.guess} pts]</span>`;
        }
      }

      breakdownHtml = `
        <div class="breakdown show-tick" id="tickPeg"><strong>Pegged this hand</strong> — ${pName('you')} ${youPeg} · ${pName('cpu')} ${cpuPeg}
          <div><button type="button" class="btn-ghost" data-action="show-peg-points">Show pegging points</button></div></div>
        ${pegOnly ? '<p>Hand and crib points do not count in speed game.</p>' : `
        <div class="breakdown show-tick" id="tickPone"><strong>${pName(pone)} hand (pone)</strong> — ${poneBlocked ? `0 (in ${poneReason})` : poneScore.total}<br>${formatNotes(poneScore.notes)}${poneBlocked ? ` <span style="color:${zoneColor(poneReason)};font-weight:600;">[${poneReason}: hole ${ls.pone.leadPegWhenScored}]</span>` : ''}
          <div><button type="button" class="btn-ghost" data-action="show-hand-points" data-key="pone">Show cards &amp; points</button></div></div>
        <div class="breakdown show-tick" id="tickDeal"><strong>${(ls.dealer && ls.dealer.title) || `${pName(state.dealer)} hand (dealer)`}</strong> — ${dealBlocked ? `0 (in ${dealReason})` : dealScore.total}<br>${formatNotes(dealScore.notes)}${dealBlocked ? ` <span style="color:${zoneColor(dealReason)};font-weight:600;">[${dealReason}: hole ${ls.dealer.leadPegWhenScored}]</span>` : ''}
          <div><button type="button" class="btn-ghost" data-action="show-hand-points" data-key="dealer">Show cards &amp; points</button></div></div>
        <div class="breakdown show-tick" id="tickCrib"><strong>${(ls.crib && ls.crib.title) || (ls.crib && ls.crib.stolen ? `Crib (Stolen by ${pName(ls.crib.stolenBy)})` : 'Crib')}</strong> — ${cribBlocked ? `0 (in ${cribReason})` : cribSigned}<br>${formatNotes(cribNotes)}${settings.optRockCrib ? ' (rocked)' : ''}${settings.optShotgunCribbage ? ' (Shotgun Cribbage: not counted)' : ''}${stolenTag}${cribBlocked ? ` <span style="color:${zoneColor(cribReason)};font-weight:600;">[${cribReason}: hole ${ls.crib.leadPegWhenScored}]</span>` : ''}
          <div><button type="button" class="btn-ghost" data-action="show-hand-points" data-key="crib">Show cards &amp; points</button></div></div>
        `}
        <div id="handDetail"></div>
        <div class="breakdown show-tick" id="tickTot"><strong>This hand total</strong> — ${pName('you')} ${youPeg + youShow} · ${pName('cpu')} ${cpuPeg + cpuShow}</div>
      `;
      scoresHtml = `<p>${pName('you')} ${state.scores ? state.scores.you : 0} · ${pName('cpu')} ${state.scores ? state.scores.cpu : 0}</p>`;
      animIds.push('tickPone', 'tickDeal', 'tickCrib', 'tickTot');
    } else {
      let pegTotalsStr = '';
      if (isPart) {
        pegTotalsStr = `Team 1 ${(state.handPeg && state.handPeg.team1) || 0} · Team 2 ${(state.handPeg && state.handPeg.team2) || 0}`;
      } else if (typeof window !== 'undefined' && typeof window.getActiveSeats === 'function') {
        pegTotalsStr = window.getActiveSeats().map(s => `${s.name} ${(state.handPeg && state.handPeg[s.id]) || 0}`).join(' · ');
      }

      let showRows = '';
      const q = state.showQueue || [];
      const zoneColor = (r) => (r === 'Bunker' ? '#eab308' : (r === 'Last 5 Peg Out' ? '#f43f5e' : '#f97316'));
      q.forEach((key) => {
        const pack = ls[key];
        if (!pack) return;
        const tid = 'tick_' + key;
        animIds.push(tid);
        const isCrib = key === 'crib';
        const notes = (pack.score && pack.score.notes) || [];
        const isBlocked = pack.blockedByZone;
        const bReason = pack.blockedReason || 'Peg Zone';
        const ptsDisplay = isBlocked ? `0 (in ${bReason})` : (isCrib ? pack.signed : pack.score.total);
        showRows += `
          <div class="breakdown show-tick" id="${tid}">
            <strong>${pack.title}</strong> — ${ptsDisplay}<br>
            ${formatNotes(notes)}${isCrib && settings.optRockCrib ? ' (rocked)' : ''}${isCrib && settings.optShotgunCribbage ? ' (Shotgun Cribbage: not counted)' : ''}${isBlocked ? ` <span style="color:${zoneColor(bReason)};font-weight:600;">[${bReason}: hole ${pack.leadPegWhenScored}]</span>` : ''}
            <div><button type="button" class="btn-ghost" data-action="show-hand-points" data-key="${key}">Show cards &amp; points</button></div>
          </div>
        `;
      });

      breakdownHtml = `
        <div class="breakdown show-tick" id="tickPeg"><strong>Pegged this hand</strong> — ${pegTotalsStr}
          <div><button type="button" class="btn-ghost" data-action="show-peg-points">Show pegging points</button></div></div>
        ${pegOnly ? '<p>Hand and crib points do not count in speed game.</p>' : showRows}
        <div id="handDetail"></div>
      `;

      if (isPart) {
        if (typeof window !== 'undefined' && typeof window.is3Player1vs2 === 'function' && window.is3Player1vs2()) {
          scoresHtml = `<p><strong>Match Score:</strong> ${pName('team1')}: ${state.scores.team1 || 0} · ${pName('team2')}: ${state.scores.team2 || 0}</p>`;
        } else if (count === 5 && settings.gameMode === 'shifting') {
          scoresHtml = `<p><strong>Match Score:</strong> ${pName('team1')}: ${state.scores.team1 || 0} · ${pName('team2')}: ${state.scores.team2 || 0} · ${pName('team3')}: ${state.scores.team3 || 0}</p>`;
        } else if (count === 6) {
          scoresHtml = `<p><strong>Match Score:</strong> ${pName('team1')}: ${state.scores.team1 || 0} · ${pName('team2')}: ${state.scores.team2 || 0} · ${pName('team3')}: ${state.scores.team3 || 0}</p>`;
        } else if (count === 7 && settings.gameMode === 'shifting') {
          scoresHtml = `<p><strong>Match Score:</strong> ${pName('team1')}: ${state.scores.team1 || 0} · ${pName('team2')}: ${state.scores.team2 || 0} · ${pName('team3')}: ${state.scores.team3 || 0} · ${pName('team4')}: ${state.scores.team4 || 0}</p>`;
        } else if (count === 8) {
          scoresHtml = `<p><strong>Match Score:</strong> ${pName('team1')}: ${state.scores.team1 || 0} · ${pName('team2')}: ${state.scores.team2 || 0} · ${pName('team3')}: ${state.scores.team3 || 0} · ${pName('team4')}: ${state.scores.team4 || 0}</p>`;
        } else {
          scoresHtml = `<p><strong>Match Score:</strong> Team 1 (Seats 1 &amp; 3): ${state.scores.team1 || 0} · Team 2 (Seats 2 &amp; 4): ${state.scores.team2 || 0}</p>`;
        }
      } else if (typeof window !== 'undefined' && typeof window.getActiveSeats === 'function') {
        scoresHtml = `<p><strong>Match Scores:</strong> ` + window.getActiveSeats().map(s => `${s.name}: ${state.scores[s.id] || 0}`).join(' · ') + `</p>`;
      }
    }

    // Check for Hand of Glory celebration (20+ pts standard, 30+ in Crazy mode)
    let gloryBannerHtml = '';
    const gloryThreshold = (settings.brand === 'crazy' && settings.optJokers) ? 30 : 20;
    let maxGloryPts = 0;
    let maxGloryKey = null;
    let maxGloryOwner = '';

    const checkGloryEntry = (k, entry) => {
      if (!entry) return;
      const pts = typeof entry.signed === 'number' ? entry.signed : (entry.score && entry.score.total) || 0;
      if (pts >= gloryThreshold && pts > maxGloryPts) {
        maxGloryPts = pts;
        maxGloryKey = k;
        maxGloryOwner = entry.title || pName(entry.who || k);
      }
    };

    if (count === 2) {
      checkGloryEntry('pone', ls.pone);
      checkGloryEntry('dealer', ls.dealer);
      checkGloryEntry('crib', ls.crib);
    } else {
      Object.keys(ls).forEach(k => checkGloryEntry(k, ls[k]));
    }

    // Keep track of the highest hand of the match
    const trackBest = (entry) => {
      if (!entry) return;
      const pts = typeof entry.signed === 'number' ? entry.signed : (entry.score && entry.score.total) || 0;
      const curBest = state.bestMatchHand ? (typeof state.bestMatchHand.signed === 'number' ? state.bestMatchHand.signed : (state.bestMatchHand.score && state.bestMatchHand.score.total) || 0) : -1;
      if (pts > curBest) {
        try { state.bestMatchHand = JSON.parse(JSON.stringify(entry)); } catch (e) { state.bestMatchHand = entry; }
      }
    };
    if (count === 2) {
      trackBest(ls.pone); trackBest(ls.dealer); trackBest(ls.crib);
    } else {
      Object.keys(ls).forEach(k => trackBest(ls[k]));
    }

    if (maxGloryKey && !pegOnly) {
      gloryBannerHtml = `
        <div class="glory-show-banner">
          <div class="glory-show-banner-text">
            <span class="glory-show-icon">🌟</span>
            <span><strong>HAND OF GLORY!</strong> ${maxGloryOwner} scored <strong>${maxGloryPts} pts</strong></span>
          </div>
          <button type="button" class="btn-glory-banner" data-action="hand-of-glory" data-seat="${maxGloryKey}" title="Generate and share Hand of Glory card">
            🏆 Share Card
          </button>
        </div>
      `;
    }

    let progressiveCutHtml = '';
    if (settings.optProgressiveCut && count === 2) {
      const handsDone = state.handsCompleted || 0;
      const currentCutLevel = Math.max(1, Math.floor((handsDone - 1) / 2) + 1);
      const nextCutLevel = Math.floor(handsDone / 2) + 1;
      const isUneven = handsDone % 2 !== 0;
      const target = settings.target || 121;
      const s1 = state.scores ? (state.scores.p1 || state.scores.you || 0) : 0;
      const s2 = state.scores ? (state.scores.p2 || state.scores.cpu || 0) : 0;
      const hasPassedTarget = s1 > 121 || s2 > 121 || s1 >= target || s2 >= target;
      const isTied = s1 === s2;

      if (isUneven && hasPassedTarget) {
        progressiveCutHtml = `
          <div style="margin: 8px 0 12px; padding: 10px 14px; background: rgba(245,158,11,0.18); border: 1.5px solid #f59e0b; border-radius: 8px; font-size: 0.88rem; color: #fde68a; line-height: 1.4;">
            ⚡ <strong>Progressive Cut: Level ${currentCutLevel} (${currentCutLevel} cut card${currentCutLevel > 1 ? 's' : ''})</strong><br>
            Target reached! Uneven cribs (${handsDone} hand played) — playing 1 more hand for equal cribs!
          </div>
        `;
      } else if (!isUneven && isTied && hasPassedTarget) {
        progressiveCutHtml = `
          <div style="margin: 8px 0 12px; padding: 10px 14px; background: rgba(239,68,68,0.18); border: 1.5px solid #ef4444; border-radius: 8px; font-size: 0.88rem; color: #fca5a5; line-height: 1.4;">
            ⚔️ <strong>Progressive Cut: Tied at ${s1} pts after even cribs!</strong><br>
            Carrying on to Level ${nextCutLevel} (${nextCutLevel} cut cards)!
          </div>
        `;
      } else {
        progressiveCutHtml = `
          <div style="margin: 8px 0 12px; padding: 8px 12px; background: rgba(240,198,74,0.1); border: 1px solid rgba(240,198,74,0.3); border-radius: 8px; font-size: 0.84rem; color: #f4ead4;">
            🃏 <strong>Progressive Cut: Level ${currentCutLevel} (${currentCutLevel} cut card${currentCutLevel > 1 ? 's' : ''})</strong> · Hand ${handsDone} completed${nextCutLevel > currentCutLevel ? ` ➔ Advancing to Level ${nextCutLevel} (${nextCutLevel} cut cards)` : ''}
          </div>
        `;
      }
    }

    let chutesAndLaddersHtml = '';
    if (state.chutesAndLaddersTriggered) {
      const tiedScore = state.chutesAndLaddersTiedScore || 0;
      chutesAndLaddersHtml = `
        <div style="margin: 8px 0 12px; padding: 12px 16px; background: linear-gradient(135deg, rgba(239,68,68,0.22), rgba(245,158,11,0.22)); border: 2px solid #f59e0b; border-radius: 10px; font-size: 0.95rem; color: #fef3c7; line-height: 1.45; text-align: center; box-shadow: 0 4px 12px rgba(0,0,0,0.35);">
          <div style="font-size: 1.3rem; margin-bottom: 4px;">🎢 <strong>CHUTES AND LADDERS!</strong> 🪜</div>
          <div>All players were tied at <strong>${tiedScore} pts</strong>!</div>
          <div style="font-weight: bold; color: #fde68a; margin-top: 4px;">Everyone slides all the way back to Start (Hole 0)!</div>
        </div>
      `;
    }

    if (typeof window !== 'undefined' && typeof window.showModal === 'function') {
      window.showModal(
        `<h2>${pegOnly ? 'Pegging only' : 'Hand scored'}</h2>
         ${gloryBannerHtml}
         ${chutesAndLaddersHtml}
         ${progressiveCutHtml}
         ${breakdownHtml}
         ${scoresHtml}
         ${actionBtn}`
      );
      ShowModal.bindModalEvents();
    }

    animIds.forEach((id, i) => {
      setTimeout(() => { const n = document.getElementById(id); if (n) n.classList.add('on'); }, 180 + i * 280);
    });

    if (typeof window !== 'undefined') {
      const checkGameOverFn = options.checkGameOver || (typeof window.checkGameOver === 'function' ? window.checkGameOver : null);
      const isOver = typeof checkGameOverFn === 'function' ? checkGameOverFn(options) : false;
      if (isOver || (state && state.over)) {
        state.showModal = null;
        state.showMeter = null;
        return;
      }
      const nextBtn = document.getElementById('btnNextHand');
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          state.showModal = null;
          state.chutesAndLaddersTriggered = false;
          state.chutesAndLaddersTiedScore = null;
          if (typeof window.hideModal === 'function') window.hideModal();
          if (typeof window.nextSeat === 'function') state.dealer = window.nextSeat(state.dealer);
          state.revealCrib = false;
          if (typeof window.newHand === 'function') window.newHand();
          if (typeof window.netPush === 'function') window.netPush({ modal: 'hide' });
        });
      }
      if (typeof window.render === 'function') window.render();
      if (typeof window.netPush === 'function') window.netPush({ modal: 'show', pegOnly: !!pegOnly });
    }
  }

  /**
   * Sets the active guess value in the Stolen Crib modal.
   * @param {number} n
   */
  static selectStolenChip(n) {
    if (typeof document === 'undefined') return;
    const inp = document.getElementById('stolenCribGuess');
    if (inp) inp.value = n;
    document.querySelectorAll('.stolen-crib-chip').forEach(c => {
      c.classList.toggle('active', c.textContent.trim() === String(n));
    });
  }

  /**
   * Displays the Stolen Crib guessing modal for human players.
   * @param {string} poneSeat
   * @param {string} dealerSeat
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static openStolenCribPrompt(poneSeat, dealerSeat, actIdx, context = {}) {
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const showModal = context.showModal || (typeof window !== 'undefined' ? window.showModal : () => {});
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const sendStolenCribGuess = context.sendStolenCribGuess || (typeof window !== 'undefined' ? window.sendStolenCribGuess : ShowModal.sendStolenCribGuess);

    const pTitle = seatName(poneSeat);
    const dTitle = seatName(dealerSeat);
    const html = `
      <div class="stolen-crib-box">
        <div class="stolen-crib-badge">Bonus: Stolen Crib</div>
        <h2>Steal the Crib?</h2>
        <p><strong>${pTitle}</strong>, guess how many points <strong>${dTitle}</strong>'s crib is worth!</p>
        <p style="font-size:0.9rem;color:#cbd5e1;margin-top:-4px;">If your guess is exactly correct, you steal and score all the points instead of the dealer.</p>
        <div class="stolen-crib-input-wrap">
          <input type="number" id="stolenCribGuess" class="stolen-crib-input" min="0" max="60" value="4" autofocus>
          <button type="button" class="btn-gold" id="btnSubmitStolenGuess">Lock in guess</button>
        </div>
        <div class="stolen-crib-quick-row">
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="0">0</button>
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="2">2</button>
          <button type="button" class="stolen-crib-chip active" data-action="select-stolen-chip" data-amount="4">4</button>
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="6">6</button>
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="8">8</button>
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="10">10</button>
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="12">12</button>
          <button type="button" class="stolen-crib-chip" data-action="select-stolen-chip" data-amount="16">16</button>
        </div>
      </div>
    `;
    showModal(html, false);
    ShowModal.bindModalEvents();
    if (typeof document === 'undefined') return;
    const btn = document.getElementById('btnSubmitStolenGuess');
    const inp = document.getElementById('stolenCribGuess');
    if (btn && inp) {
      btn.addEventListener('click', () => {
        const val = parseInt(inp.value, 10) || 0;
        hideModal();
        sendStolenCribGuess(val, actIdx, context);
      });
      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const val = parseInt(inp.value, 10) || 0;
          hideModal();
          sendStolenCribGuess(val, actIdx, context);
        }
      });
    }
  }

  /**
   * Displays the interactive Twisted Cribbage modal allowing the player to arrange
   * all 8 cards into two 4-card hands maximizing total points.
   * @param {string} dealerSeat
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static openTwistedCribbageModal(dealerSeat, actIdx, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getSeatKept = context.getSeatKept || (typeof window !== 'undefined' && typeof window.getSeatKept === 'function' ? window.getSeatKept : () => []);
    const pName = context.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (s) => s);
    const showModal = context.showModal || (typeof window !== 'undefined' && typeof window.showModal === 'function' ? window.showModal : () => {});
    const hideModal = context.hideModal || (typeof window !== 'undefined' && typeof window.hideModal === 'function' ? window.hideModal : () => {});
    const resolveTwistedCribbageSplit = context.resolveTwistedCribbageSplit || (typeof window !== 'undefined' && typeof window.resolveTwistedCribbageSplit === 'function' ? window.resolveTwistedCribbageSplit : ShowModal.resolveTwistedCribbageSplit);

    const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
    const starterArg = (starters.length > 1) ? starters : state.starter;
    const Scorer = (typeof CC !== 'undefined' && CC?.CribbageScorer) || (typeof window !== 'undefined' ? window.CribbageScorer : null) || CribbageScorer;

    // Retrieve 8 cards: either from state.twisted8Cards or combining kept + crib
    const originalKept = (state.originalDealerKept && state.originalDealerKept.length) ? [...state.originalDealerKept] : (getSeatKept(dealerSeat) || []);
    const originalCrib = (state.originalCrib && state.originalCrib.length) ? [...state.originalCrib] : (state.crib || []);
    let all8 = (state.twisted8Cards && state.twisted8Cards.length === 8)
      ? [...state.twisted8Cards]
      : [...originalKept, ...originalCrib];

    if (all8.length !== 8) {
      all8 = (all8.length >= 8) ? all8.slice(0, 8) : all8;
    }

    const is3D = Boolean(
      (settings && (settings.boardViewMode === '3d' || settings.boardViewMode === 'room3d' || settings.boardViewMode === 'room3d-fullscreen')) ||
      (typeof window !== 'undefined' && window.settings && (window.settings.boardViewMode === '3d' || window.settings.boardViewMode === 'room3d' || window.settings.boardViewMode === 'room3d-fullscreen')) ||
      (typeof document !== 'undefined' && (
        document.getElementById('board3DCanvas') ||
        document.getElementById('room3dCanvas') ||
        document.querySelector('.board-3d-wrap') ||
        document.querySelector('.room3d-wrap')
      ))
    );
    if (is3D) {
      const optimal = (Scorer && typeof Scorer.findOptimalTwistedSplit === 'function')
        ? Scorer.findOptimalTwistedSplit(all8, starterArg, settings)
        : { hand1: all8.slice(0, 4), hand2: all8.slice(4, 8) };
      resolveTwistedCribbageSplit(optimal.hand1, optimal.hand2, actIdx, context);
      return;
    }

    let hand1 = all8.slice(0, 4);
    let hand2 = all8.slice(4, 8);
    let selected = null; // { handNum: 1|2, idx: 0..3 }
    let toastMsg = '';

    const scoreOpts = { ...settings, optFiveCardFlushOnly: true };

    const rankNames = { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' };
    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };

    function renderSingleCard(c, handNum, idx, isSelected) {
      const suit = c.suit || c.s || 'S';
      const isRed = suit === 'H' || suit === 'D';
      const color = isRed ? '#dc2626 !important' : '#111827 !important';
      const r = c.rankName || c.r || rankNames[c.rank] || c.rank || '★';
      const sym = suitSyms[suit] || suit || '';

      const cardBorder = isSelected ? 'border: 2px solid #f59e0b;' : 'border: 2px solid #cbd5e1;';
      const cardShadow = isSelected
        ? 'box-shadow: 0 0 16px rgba(245, 158, 11, 0.95), 0 4px 14px rgba(0,0,0,0.5); transform: translateY(-6px);'
        : 'box-shadow: 0 4px 10px rgba(0,0,0,0.35);';

      return `
        <div class="playing-card twisted-card ${isRed ? 'red card-red' : 'black card-black'}${isSelected ? ' selected' : ''}"
             id="twCard_${handNum}_${idx}"
             data-action="twisted-card"
             data-hand="${handNum}"
             data-index="${idx}"
             style="position: relative; width: 62px; height: 90px; box-sizing: border-box; background: #ffffff !important; border-radius: 8px; overflow: hidden; text-align: left; cursor: pointer; transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease; ${cardBorder} ${cardShadow}">
          ${TraditionalCard.renderHtml(c)}
          <div class="twisted-card-label" style="font-size: 0.62rem; font-weight: 700; color: #64748b !important; -webkit-text-fill-color: #64748b !important; text-align: center; text-transform: uppercase; position: absolute; bottom: 2px; left: 0; right: 0; z-index: 3;">${handNum === 1 ? 'Hand 1' : 'Hand 2'}</div>
        </div>
      `;
    }

    function renderCutCardBadge() {
      if (!starterArg) return '';
      const st = Array.isArray(starterArg) ? starterArg[0] : starterArg;
      if (!st) return '';
      const suit = st.suit || st.s || 'S';
      const isRed = suit === 'H' || suit === 'D';
      const color = isRed ? '#dc2626 !important' : '#111827 !important';
      const r = st.rankName || st.r || rankNames[st.rank] || st.rank || '★';
      const sym = suitSyms[suit] || suit || '';
      return `
        <div style="display: flex; align-items: center; justify-content: center; gap: 10px; background: rgba(234, 179, 8, 0.12); border: 1px solid rgba(234, 179, 8, 0.45); border-radius: 8px; padding: 6px 14px; margin: 10px auto; max-width: 540px;">
          <div class="twisted-cut-card ${isRed ? 'red card-red' : 'black card-black'}" style="width: 32px; height: 46px; background: #ffffff !important; border: 2px solid #eab308; border-radius: 5px; display: flex; flex-direction: column; justify-content: center; align-items: center; font-weight: 900; line-height: 1; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
            <span class="twisted-cut-rank" style="font-size: 0.78rem; color: ${color}; -webkit-text-fill-color: ${color};">${r}</span>
            <span class="twisted-cut-suit" style="font-size: 0.92rem; color: ${color}; -webkit-text-fill-color: ${color};">${sym}</span>
          </div>
          <div style="text-align: left;">
            <div style="font-size: 0.85rem; color: #fde047; font-weight: 800;">Starter Cut Card: <span style="font-weight: 900; color: ${color}; -webkit-text-fill-color: ${color};">${r}${sym}</span></div>
            <div style="font-size: 0.74rem; color: #cbd5e1;">⚠️ 5-Card Flush Rule: Flushes in either hand require all 5 cards (4 in hand + cut) matching suit.</div>
          </div>
        </div>
      `;
    }

    function renderModalContent() {
      const s1 = (hand1.length === 4) ? Scorer.scoreHand(hand1, starterArg, true, scoreOpts) : { total: 0, notes: [] };
      const s2 = (hand2.length === 4) ? Scorer.scoreHand(hand2, starterArg, true, { ...scoreOpts, dealerHand: hand1 }) : { total: 0, notes: [] };
      const totalPts = s1.total + s2.total;

      const formatNotesList = (notes) => {
        if (!notes || !notes.length) return '<span style="color:#64748b;">No combinations</span>';
        return notes.map(n => `<span style="display:inline-block; margin-right:8px; color:#cbd5e1;">${n}</span>`).join('· ');
      };

      const h1CardsHtml = hand1.map((c, i) => renderSingleCard(c, 1, i, selected && selected.handNum === 1 && selected.idx === i)).join('');
      const h2CardsHtml = hand2.map((c, i) => renderSingleCard(c, 2, i, selected && selected.handNum === 2 && selected.idx === i)).join('');

      return `
        <div class="twisted-modal-box">
          <div class="twisted-badge">🌀 TWISTED CRIBBAGE — DEALER COUNT</div>
          <h2 class="twisted-modal-title">Arrange Your 8 Cards into Two 4-Card Hands</h2>
          <div class="twisted-modal-subtitle">
            ${pName(dealerSeat)}, combine all 8 cards from your hand and crib to maximize your combined score!
          </div>

          ${renderCutCardBadge()}

          <div class="twisted-scoreboard">
            <div style="text-align:right;">
              <div style="font-size:0.78rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Hand 1 (Dealer)</div>
              <div style="font-size:1.4rem; font-weight:800; color:#60a5fa;">+${s1.total} pts</div>
            </div>
            <div style="font-size:1.8rem; font-weight:900; color:#64748b;">+</div>
            <div style="text-align:left;">
              <div style="font-size:0.78rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Hand 2 (Crib)</div>
              <div style="font-size:1.4rem; font-weight:800; color:#c084fc;">+${s2.total} pts</div>
            </div>
            <div style="font-size:1.8rem; font-weight:900; color:#64748b;">=</div>
            <div style="border-left:2px solid rgba(255,255,255,0.15); padding-left:16px; text-align:left;">
              <div style="font-size:0.78rem; text-transform:uppercase; color:#fde047; font-weight:800; letter-spacing:0.05em;">COMBINED TOTAL</div>
              <div class="twisted-score-total">${totalPts} PTS</div>
            </div>
          </div>

          ${toastMsg ? `<div style="margin: 6px auto 10px; color:#38bdf8; font-weight:700; font-size:0.85rem;">${toastMsg}</div>` : ''}

          <div style="font-size: 0.78rem; color: #94a3b8; margin-bottom: 8px;">
            💡 <em>Click a card in each hand to swap them.</em>
          </div>

          <div class="twisted-hands-grid">
            <div class="twisted-hand-panel hand-1">
              <div class="twisted-hand-header">
                <span class="twisted-hand-name">HAND 1 (DEALER)</span>
                <span class="twisted-hand-pts">${s1.total} pts</span>
              </div>
              <div class="twisted-cards-row">
                ${h1CardsHtml}
              </div>
              <div class="twisted-notes">
                ${formatNotesList(s1.notes)}
              </div>
            </div>

            <div class="twisted-hand-panel hand-2">
              <div class="twisted-hand-header">
                <span class="twisted-hand-name">HAND 2 (CRIB)</span>
                <span class="twisted-hand-pts">${s2.total} pts</span>
              </div>
              <div class="twisted-cards-row">
                ${h2CardsHtml}
              </div>
              <div class="twisted-notes">
                ${formatNotesList(s2.notes)}
              </div>
            </div>
          </div>

          <div class="twisted-actions">
            <button type="button" class="btn-twisted-best" id="btnTwistedAutoBest" data-action="twisted-best">
              ⚡ Auto Best Split
            </button>
            <button type="button" class="btn-ghost" id="btnTwistedReset" data-action="twisted-reset" style="padding:10px 18px; border-radius:8px; color:#cbd5e1;">
              ↺ Original Hands
            </button>
            <button type="button" class="btn-twisted-confirm" id="btnTwistedConfirm" data-action="twisted-confirm">
              ✔ Confirm Hands (+${totalPts} pts)
            </button>
          </div>
        </div>
      `;
    }

    const handleTwistedClick = (e) => {
      const cardEl = e.target.closest('[data-action="twisted-card"], .twisted-card');
      if (cardEl && cardEl.dataset.hand !== undefined && cardEl.dataset.index !== undefined) {
        const handNum = parseInt(cardEl.dataset.hand, 10);
        const idx = parseInt(cardEl.dataset.index, 10);
        if (typeof window.onTwistedCardClick === 'function') {
          window.onTwistedCardClick(handNum, idx);
        }
        return;
      }
      const bestBtn = e.target.closest('#btnTwistedAutoBest, [data-action="twisted-best"]');
      if (bestBtn) {
        if (typeof window.onTwistedAutoBest === 'function') {
          window.onTwistedAutoBest();
        }
        return;
      }
      const resetBtn = e.target.closest('#btnTwistedReset, [data-action="twisted-reset"]');
      if (resetBtn) {
        if (typeof window.onTwistedReset === 'function') {
          window.onTwistedReset();
        }
        return;
      }
      const confirmBtn = e.target.closest('#btnTwistedConfirm, [data-action="twisted-confirm"]');
      if (confirmBtn) {
        if (typeof window.onTwistedConfirm === 'function') {
          window.onTwistedConfirm();
        }
        return;
      }
    };

    function refreshModal() {
      const container = (typeof document !== 'undefined') ? (document.getElementById('modalContent') || document.getElementById('modal')) : null;
      if (container) {
        container.innerHTML = renderModalContent();
      } else {
        showModal(renderModalContent(), false);
      }
    }

    window.onTwistedCardClick = (handNum, idx) => {
      toastMsg = '';
      if (!selected) {
        selected = { handNum, idx };
      } else if (selected.handNum === handNum && selected.idx === idx) {
        selected = null;
      } else if (selected.handNum === handNum) {
        selected = { handNum, idx };
      } else {
        const idx1 = selected.handNum === 1 ? selected.idx : idx;
        const idx2 = selected.handNum === 2 ? selected.idx : idx;
        const temp = hand1[idx1];
        hand1[idx1] = hand2[idx2];
        hand2[idx2] = temp;
        selected = null;
      }
      refreshModal();
    };

    window.onTwistedAutoBest = () => {
      const optimal = Scorer.findOptimalTwistedSplit(all8, starterArg, settings);
      if (optimal && optimal.hand1 && optimal.hand2) {
        hand1 = [...optimal.hand1];
        hand2 = [...optimal.hand2];
        selected = null;
        toastMsg = `✨ Optimal split loaded (+${optimal.total} pts)!`;
        refreshModal();
      }
    };

    window.onTwistedReset = () => {
      hand1 = all8.slice(0, 4);
      hand2 = all8.slice(4, 8);
      selected = null;
      toastMsg = '↺ Reset to original hand & crib.';
      refreshModal();
    };

    window.onTwistedConfirm = () => {
      const container = (typeof document !== 'undefined') ? (document.getElementById('modalContent') || document.getElementById('modal')) : null;
      if (container) {
        container.removeEventListener('click', handleTwistedClick);
      }
      delete window.onTwistedCardClick;
      delete window.onTwistedAutoBest;
      delete window.onTwistedReset;
      delete window.onTwistedConfirm;

      hideModal();
      resolveTwistedCribbageSplit(hand1, hand2, actIdx, context);
    };

    showModal(renderModalContent(), false);
    const modalContainer = (typeof document !== 'undefined') ? (document.getElementById('modalContent') || document.getElementById('modal')) : null;
    if (modalContainer) {
      modalContainer.addEventListener('click', handleTwistedClick);
    }
  }

  /**
   * Resolves the dealer's Twisted Cribbage split, updates hands, scores, and advances the show act.
   * @param {Array} hand1
   * @param {Array} hand2
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static resolveTwistedCribbageSplit(hand1, hand2, actIdx, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const setSeatKept = context.setSeatKept || (typeof window !== 'undefined' && typeof window.setSeatKept === 'function' ? window.setSeatKept : null);
    const pName = context.pName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName : (s) => s);
    const seatName = context.seatName || (typeof window !== 'undefined' && typeof window.seatName === 'function' ? window.seatName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' && typeof window.log === 'function' ? window.log : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' && typeof window.fancyCall === 'function' ? window.fancyCall : () => {});
    const render = context.render || (typeof window !== 'undefined' && typeof window.render === 'function' ? window.render : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' && typeof window.netPush === 'function' ? window.netPush : () => {});
    const beginShowAct = context.beginShowAct || (typeof window !== 'undefined' && typeof window.beginShowAct === 'function' ? window.beginShowAct : ShowModal.beginShowAct);

    const dealerSeat = state.dealer === 'you' ? 'p1' : (state.dealer === 'cpu' ? 'p2' : state.dealer);
    const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
    const starterArg = (starters.length > 1) ? starters : state.starter;

    const Scorer = (typeof CC !== 'undefined' && CC?.CribbageScorer) || (typeof window !== 'undefined' ? window.CribbageScorer : null) || CribbageScorer;
    const scoreOpts = { ...settings, optFiveCardFlushOnly: true };
    const score1 = Scorer.scoreHand(hand1, starterArg, true, scoreOpts);
    const score2 = Scorer.scoreHand(hand2, starterArg, true, { ...scoreOpts, dealerHand: hand1 });

    state.twistedCribbageResolved = true;

    // Update dealer kept hand and crib cards
    if (typeof setSeatKept === 'function') {
      setSeatKept(dealerSeat, hand1, state);
    } else if (state.seatKept) {
      state.seatKept[dealerSeat] = hand1;
    }
    state.crib = hand2;

    const pegOnly = settings.target === 31;
    const isShotgun = Boolean(settings && settings.optShotgunCribbage);
    const cribSigned = (pegOnly || isShotgun) ? 0 : (settings.optRockCrib ? -score2.total : score2.total);

    if (!state.lastShow) state.lastShow = {};
    state.lastShow.dealer = {
      title: `${pName(dealerSeat)} hand (Twisted 1/2)`,
      cards: hand1,
      starter: state.starter,
      starters,
      score: score1,
      signed: score1.total,
      who: dealerSeat
    };
    state.lastShow.crib = {
      title: isShotgun ? 'Crib (Shotgun Cribbage)' : 'Crib (Twisted 2/2)',
      cards: hand2,
      starter: state.starter,
      starters,
      score: score2,
      signed: cribSigned,
      who: dealerSeat
    };

    const totalPts = score1.total + score2.total;
    log(`🌀 Twisted Cribbage: ${seatName(dealerSeat)} arranged 8 cards into two hands (+${score1.total} & +${score2.total} = ${totalPts} pts)!`);
    fancyCall(`Twisted: +${totalPts} pts!`, 'fifteen', 1200);

    netPush({
      op: 'twistedCribbageSplit',
      hand1,
      hand2,
      totalPts
    });

    render();

    // Now resume show act
    beginShowAct(actIdx, context);
  }

  /**
   * Displays the interactive Muggins manual hand/crib counting modal for human players.
   * @param {string} key 'pone', 'dealer', or 'crib'
   * @param {Object} pack
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static openMugginsCountPrompt(key, pack, actIdx, context = {}) {
    const showModal = context.showModal || (typeof window !== 'undefined' ? window.showModal : () => {});
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const beginShowAct = context.beginShowAct || (typeof window !== 'undefined' ? window.beginShowAct : ShowModal.beginShowAct);
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);

    const isCrib = key === 'crib';
    const cards = pack.cards || [];
    const starters = (pack.starters && pack.starters.length) ? pack.starters : (pack.starter ? [pack.starter] : []);
    const suitSyms = { H: '♥', D: '♦', S: '♠', C: '♣' };
    const rankNames = { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' };

    const renderCard = (c, isCut = false) => {
      if (!c) return '';
      const suit = c.suit || c.s || 'S';
      const isRed = suit === 'H' || suit === 'D';
      const color = isRed ? '#dc2626 !important' : '#111827 !important';
      const r = c.rankName || c.r || rankNames[c.rank] || c.rank || '★';
      const s = suitSyms[suit] || suit || '';
      return `
        <div style="position: relative; width: 56px; height: 80px; box-sizing: border-box; background: #ffffff !important; border: 2px solid ${isCut ? '#eab308' : '#cbd5e1'}; border-radius: 6px; display: flex; flex-direction: column; justify-content: space-between; padding: 4px 5px; box-shadow: 0 3px 8px rgba(0,0,0,0.35); text-align: left;">
          <div style="font-size: 0.95rem; font-weight: 900; color: ${color}; -webkit-text-fill-color: ${color}; line-height: 1;">${r}<span style="font-size: 0.78rem; margin-left: 2px;">${s}</span></div>
          <div style="font-size: 1.5rem; color: ${color}; -webkit-text-fill-color: ${color}; line-height: 1; text-align: center;">${s}</div>
          <div style="font-size: 0.58rem; font-weight: 800; color: ${isCut ? '#ca8a04' : 'transparent'}; -webkit-text-fill-color: ${isCut ? '#ca8a04' : 'transparent'}; text-align: center; text-transform: uppercase;">${isCut ? 'CUT' : ''}</div>
        </div>
      `;
    };

    const cardsHtml = cards.map(c => renderCard(c, false)).join('');
    const starterHtml = starters.map(s => renderCard(s, true)).join('');

    const html = `
      <div class="muggins-count-box" style="max-width: 480px; width: 100%; margin: 0 auto; text-align: center; color: #f4ead4; font-family: inherit;">
        <div style="display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #f0c64a, #d4a017); color: #1a1208; font-size: 1.5rem; margin-bottom: 8px;">⚡</div>
        <h2 style="margin: 0 0 4px; font-size: 1.3rem; font-weight: 800; color: #f0c64a; letter-spacing: 0.03em;">
          MUGGINS: COUNT YOUR ${isCrib ? 'CRIB' : 'HAND'}
        </h2>
        <p style="margin: 0 0 12px; font-size: 0.84rem; color: #cbd5e1; line-height: 1.4;">
          ${pName(pack.who)}, examine your cards and the starter card${starters.length > 1 ? 's' : ''} below.
          <br><span style="color: #f87171; font-weight: 600;">⚠️ If you undercount, your opponent can call Muggins and steal the missed points!</span>
        </p>

        <!-- Cards Display (Hand + Cut Starter) -->
        <div style="display: flex; justify-content: center; align-items: center; gap: 6px; margin-bottom: 14px; flex-wrap: wrap;">
          <div style="display: flex; gap: 5px; justify-content: center; flex-wrap: wrap;">
            ${cardsHtml}
          </div>
          ${starterHtml ? `
            <div style="display: flex; align-items: center; margin: 0 4px; color: #ca8a04; font-size: 1.2rem; font-weight: 800;">+</div>
            <div style="display: flex; gap: 5px; justify-content: center; flex-wrap: wrap;">${starterHtml}</div>
          ` : ''}
        </div>

        <!-- Point Input & Stepper -->
        <div style="display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 12px;">
          <button type="button" id="btnMugginsDec" style="width: 42px; height: 42px; font-size: 1.4rem; font-weight: 800; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.25); background: rgba(255,255,255,0.08); color: #f4ead4; cursor: pointer;">−</button>
          <input type="number" id="mugginsCountInp" min="0" max="60" value="0"
                 style="width: 80px; height: 42px; font-size: 1.7rem; font-weight: 900; text-align: center; border-radius: 8px; border: 2px solid #f0c64a; background: rgba(0,0,0,0.6); color: #f0c64a !important; -webkit-text-fill-color: #f0c64a !important; outline: none;">
          <button type="button" id="btnMugginsInc" style="width: 42px; height: 42px; font-size: 1.4rem; font-weight: 800; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.25); background: rgba(255,255,255,0.08); color: #f4ead4; cursor: pointer;">+</button>
        </div>

        <!-- Quick Select Chips -->
        <div style="display: flex; flex-wrap: wrap; justify-content: center; gap: 6px; margin-bottom: 16px;">
          ${[0, 2, 4, 6, 7, 8, 9, 10, 12, 14, 15, 16, 20].map(n => `
            <button type="button" class="muggins-count-chip" data-val="${n}"
                    style="padding: 4px 10px; font-size: 0.84rem; font-weight: 700; border-radius: 14px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.06); color: #f4ead4; cursor: pointer;">
              ${n}
            </button>
          `).join('')}
        </div>

        <!-- Action Buttons -->
        <div style="display: flex; gap: 10px; justify-content: center;">
          <button type="button" id="btnMugginsClaimCount" class="btn-gold"
                  style="padding: 10px 22px; font-size: 0.96rem; font-weight: 800; border-radius: 8px; cursor: pointer; box-shadow: 0 4px 12px rgba(240,198,74,0.35);">
            Claim <span id="mugginsClaimVal">0</span> Points
          </button>
          <button type="button" id="btnMugginsAutoCount" class="btn-ghost"
                  style="padding: 10px 14px; font-size: 0.84rem; font-weight: 600; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.08); color: #a8a29e; cursor: pointer;"
                  title="Auto-count without claiming">
            Auto-Count
          </button>
        </div>
      </div>
    `;

    showModal(html, false);

    if (typeof document === 'undefined') return;

    const inp = document.getElementById('mugginsCountInp');
    const claimValSpan = document.getElementById('mugginsClaimVal');
    const btnDec = document.getElementById('btnMugginsDec');
    const btnInc = document.getElementById('btnMugginsInc');
    const chips = document.querySelectorAll('.muggins-count-chip');
    const btnClaim = document.getElementById('btnMugginsClaimCount');
    const btnAuto = document.getElementById('btnMugginsAutoCount');

    const updateVal = (newVal) => {
      let v = Math.max(0, Math.min(60, parseInt(newVal, 10) || 0));
      if (inp) inp.value = v;
      if (claimValSpan) claimValSpan.textContent = v;
      chips.forEach(c => {
        const cv = parseInt(c.dataset.val, 10);
        if (cv === v) {
          c.style.background = '#f0c64a';
          c.style.color = '#000';
          c.style.borderColor = '#f0c64a';
        } else {
          c.style.background = 'rgba(255,255,255,0.06)';
          c.style.color = '#f4ead4';
          c.style.borderColor = 'rgba(255,255,255,0.2)';
        }
      });
    };

    if (btnDec) btnDec.addEventListener('click', () => updateVal(parseInt(inp.value, 10) - 1));
    if (btnInc) btnInc.addEventListener('click', () => updateVal(parseInt(inp.value, 10) + 1));
    if (inp) {
      inp.addEventListener('input', () => updateVal(inp.value));
      inp.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && btnClaim) btnClaim.click();
      });
    }
    chips.forEach(c => {
      c.addEventListener('click', () => updateVal(c.dataset.val));
    });

    if (btnClaim) {
      btnClaim.addEventListener('click', () => {
        const val = parseInt(inp ? inp.value : 0, 10) || 0;
        hideModal();
        pack.mugginsCountResolved = true;
        pack.claimedPoints = val;
        beginShowAct(actIdx, context);
      });
    }

    if (btnAuto) {
      btnAuto.addEventListener('click', () => {
        hideModal();
        pack.mugginsCountResolved = true;
        pack.claimedPoints = pack.score.total;
        beginShowAct(actIdx, context);
      });
    }
  }

  /**
   * Dispatches online intent or resolves guess locally.
   * @param {number} val
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static sendStolenCribGuess(val, actIdx, context = {}) {
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const netSend = context.netSend || (typeof window !== 'undefined' ? window.netSend : () => {});
    const showWait = context.showWait || (typeof window !== 'undefined' ? window.showWait : () => {});
    const resolveStolenCribGuess = context.resolveStolenCribGuess || (typeof window !== 'undefined' ? window.resolveStolenCribGuess : ShowModal.resolveStolenCribGuess);

    if (isOnline() && settings.netRole === 'guest') {
      netSend({ type: 'intent', op: 'stolenCribGuess', guess: val });
      showWait('Guess submitted! Waiting for result…');
      return;
    }
    resolveStolenCribGuess(val, actIdx, context);
  }

  /**
   * Evaluates Stolen Crib guess and applies bonus score reassignment.
   * @param {number} guess
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static resolveStolenCribGuess(guess, actIdx, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const famousFanfare = context.famousFanfare || (typeof window !== 'undefined' ? window.famousFanfare : ShowModal.famousFanfare);
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const beginShowAct = context.beginShowAct || (typeof window !== 'undefined' ? window.beginShowAct : ShowModal.beginShowAct);

    const cribPack = state.lastShow && state.lastShow.crib;
    if (!cribPack) {
      state.stolenCribResolved = true;
      beginShowAct(actIdx, context);
      return;
    }
    const dealerSeat = cribPack.who;
    const poneSeat = dealerSeat === 'p1' ? 'p2' : 'p1';
    const actual = cribPack.score.total;
    const success = (guess === actual);
    state.stolenCrib = {
      guess,
      actual,
      pone: poneSeat,
      dealer: dealerSeat,
      success
    };
    state.stolenCribResolved = true;

    if (success) {
      cribPack.who = poneSeat;
      cribPack.stolen = true;
      cribPack.stolenBy = poneSeat;
      cribPack.title = 'Stolen Crib (' + seatName(poneSeat) + ')';
      log(`STOLEN CRIB! ${seatName(poneSeat)} correctly guessed ${guess} pts and STOLE the crib!`);
      fancyCall(`Crib Stolen! (${guess} pts)`, 'famous', 2800);
      famousFanfare(actual);
      netPush({
        fancy: { text: `Crib Stolen! (${guess} pts)`, kind: 'famous', ms: 2800 },
        sfx: 'fanfare20'
      });
    } else {
      cribPack.stolen = false;
      log(`${seatName(poneSeat)} guessed ${guess} pts for the crib (actual: ${actual}). Dealer keeps the crib.`);
      fancyCall(`Crib Safe (${actual} pts)`, '', 2000);
      netPush({
        fancy: { text: `Crib Safe (Guessed ${guess})`, kind: '', ms: 2000 }
      });
    }

    setTimeout(() => {
      beginShowAct(actIdx, context);
    }, 900);
  }

  /**
   * Begins presentation of a specific hand/crib show act.
   * @param {number} i
   * @param {Object} [context]
   */
  static beginShowAct(i, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state) return;

    const setShowMeter = context.setShowMeter || (typeof window !== 'undefined' ? window.setShowMeter : ShowModal.setShowMeter);
    const openShowModal = context.openShowModal || (typeof window !== 'undefined' ? window.openShowModal : ShowModal.open);

    if (state.over) {
      state.showModal = null;
      state.showMeter = null;
      if (typeof setShowMeter === 'function') setShowMeter('', 0, '');
      if (typeof document !== 'undefined') {
        const m = document.getElementById('showMeter');
        if (m) m.classList.add('hidden');
      }
      const checkGameOverFn = context.checkGameOver || (typeof window !== 'undefined' ? window.checkGameOver : null);
      if (typeof checkGameOverFn === 'function') checkGameOverFn(context);
      return;
    }

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const getPlayerCount = context.getPlayerCount || (typeof window !== 'undefined' ? window.getPlayerCount : () => 2);
    const render = context.render || (typeof window !== 'undefined' ? window.render : () => {});
    const getSeat = context.getSeat || (typeof window !== 'undefined' ? window.getSeat : () => ({}));
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman : (s => s === 'p1'));
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const openStolenCribPrompt = context.openStolenCribPrompt || (typeof window !== 'undefined' ? window.openStolenCribPrompt : ShowModal.openStolenCribPrompt);
    const showWait = context.showWait || (typeof window !== 'undefined' ? window.showWait : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const cpuGuessCrib = context.cpuGuessCrib || (typeof window !== 'undefined' ? window.cpuGuessCrib : () => 4);
    const resolveStolenCribGuess = context.resolveStolenCribGuess || (typeof window !== 'undefined' ? window.resolveStolenCribGuess : ShowModal.resolveStolenCribGuess);
    const resetAnims = context.resetAnims || (typeof window !== 'undefined' ? window.resetAnims : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const finishShowAct = context.finishShowAct || (typeof window !== 'undefined' ? window.finishShowAct : ShowModal.finishShowAct);
    const tickShowLine = context.tickShowLine || (typeof window !== 'undefined' ? window.tickShowLine : ShowModal.tickShowLine);

    const keys = state.showQueue || [];
    if (i >= keys.length) {
      state.showAct = 'done';
      state.highlightIds = [];
      state.showMeter = null;
      setShowMeter('', 0, '');
      if (typeof document !== 'undefined') {
        const m = document.getElementById('showMeter');
        if (m) m.classList.add('hidden');
      }
      if (settings.optChutesAndLadders) {
        const checkChutesAndLadders = context.checkChutesAndLadders || (typeof window !== 'undefined' ? window.checkChutesAndLadders : GameEngine.checkChutesAndLadders);
        if (typeof checkChutesAndLadders === 'function') {
          checkChutesAndLadders(state, settings, context);
        }
      }
      if (state.over) {
        state.showModal = null;
        const checkGameOverFn = context.checkGameOver || (typeof window !== 'undefined' ? window.checkGameOver : null);
        if (typeof checkGameOverFn === 'function') checkGameOverFn(context);
        return;
      }
      openShowModal(false, context);
      return;
    }
    const key = keys[i];

    if (key === 'crib' && settings.optStolenCrib && getPlayerCount() === 2 && !state.stolenCribResolved) {
      state.revealCrib = false;
      render();
      const dealerSeat = state.dealer === 'you' ? 'p1' : (state.dealer === 'cpu' ? 'p2' : state.dealer);
      const poneSeat = dealerSeat === 'p1' ? 'p2' : 'p1';
      const poneSeatObj = getSeat(poneSeat);
      const isPoneHuman = poneSeatObj && poneSeatObj.type === 'human';

      if (isOnline()) {
        const isGuestPone = (settings.netRole === 'guest' && poneSeat === 'p2');
        const isHostPone = (settings.netRole === 'host' && poneSeat === 'p1');
        if (isGuestPone || isHostPone) {
          openStolenCribPrompt(poneSeat, dealerSeat, i, context);
        } else {
          showWait(`Waiting for ${seatName(poneSeat)}'s Stolen Crib guess…`);
        }
        return;
      }

      if (isPoneHuman) {
        openStolenCribPrompt(poneSeat, dealerSeat, i, context);
        return;
      } else {
        const cpuName = seatName(poneSeat);
        fancyCall(`${cpuName} guessing crib…`, '', 1000);
        setTimeout(() => {
          const guess = cpuGuessCrib(poneSeat, dealerSeat);
          resolveStolenCribGuess(guess, i, context);
        }, 1100);
        return;
      }
    }

    if (key === 'dealer' && settings.optTwistedCribbage && getPlayerCount() === 2 && !state.twistedCribbageResolved) {
      render();
      const dealerSeat = state.dealer === 'you' ? 'p1' : (state.dealer === 'cpu' ? 'p2' : state.dealer);
      const dealerSeatObj = getSeat(dealerSeat);
      const isDealerHuman = isSeatHuman ? isSeatHuman(dealerSeat) : (dealerSeatObj && dealerSeatObj.type === 'human');

      const is3D = Boolean(
        (settings && (settings.boardViewMode === '3d' || settings.boardViewMode === 'room3d' || settings.boardViewMode === 'room3d-fullscreen')) ||
        (typeof window !== 'undefined' && window.settings && (window.settings.boardViewMode === '3d' || window.settings.boardViewMode === 'room3d' || window.settings.boardViewMode === 'room3d-fullscreen')) ||
        (typeof document !== 'undefined' && (
          document.getElementById('board3DCanvas') ||
          document.getElementById('room3dCanvas') ||
          document.querySelector('.board-3d-wrap') ||
          document.querySelector('.room3d-wrap')
        ))
      );

      const openTwistedCribbageModalFn = context.openTwistedCribbageModal || (typeof window !== 'undefined' ? window.openTwistedCribbageModal : ShowModal.openTwistedCribbageModal);
      const resolveTwistedCribbageSplitFn = context.resolveTwistedCribbageSplit || (typeof window !== 'undefined' ? window.resolveTwistedCribbageSplit : ShowModal.resolveTwistedCribbageSplit);

      if (is3D) {
        const dealerName = seatName(dealerSeat);
        fancyCall(`${dealerName} arranging best split…`, '', 1000);
        setTimeout(() => {
          const getSeatKeptFn = context.getSeatKept || (typeof window !== 'undefined' ? window.getSeatKept : () => []);
          const combined = (state.twisted8Cards && state.twisted8Cards.length === 8)
            ? state.twisted8Cards
            : [...(getSeatKeptFn(dealerSeat) || []), ...(state.crib || [])];
          const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
          const starterArg = (starters.length > 1) ? starters : state.starter;
          const Scorer = (typeof CC !== 'undefined' && CC?.CribbageScorer) || (typeof window !== 'undefined' ? window.CribbageScorer : null) || CribbageScorer;
          const optimal = (Scorer && typeof Scorer.findOptimalTwistedSplit === 'function')
            ? Scorer.findOptimalTwistedSplit(combined, starterArg, settings)
            : { hand1: combined.slice(0, 4), hand2: combined.slice(4, 8) };
          resolveTwistedCribbageSplitFn(optimal.hand1, optimal.hand2, i, context);
        }, 600);
        return;
      }

      if (isOnline()) {
        const isGuestDealer = (settings.netRole === 'guest' && dealerSeat === 'p2');
        const isHostDealer = (settings.netRole === 'host' && dealerSeat === 'p1');
        if (isGuestDealer || isHostDealer) {
          openTwistedCribbageModalFn(dealerSeat, i, context);
        } else {
          showWait(`Waiting for ${seatName(dealerSeat)} to arrange Twisted Cribbage hands…`);
        }
        return;
      }

      if (isDealerHuman) {
        openTwistedCribbageModalFn(dealerSeat, i, context);
        return;
      } else {
        const cpuName = seatName(dealerSeat);
        fancyCall(`${cpuName} calculating best split…`, '', 1000);
        setTimeout(() => {
          const getSeatKeptFn = context.getSeatKept || (typeof window !== 'undefined' ? window.getSeatKept : () => []);
          const combined = (state.twisted8Cards && state.twisted8Cards.length === 8)
            ? state.twisted8Cards
            : [...(getSeatKeptFn(dealerSeat) || []), ...(state.crib || [])];
          const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
          const starterArg = (starters.length > 1) ? starters : state.starter;
          const Scorer = (typeof CC !== 'undefined' && CC?.CribbageScorer) || (typeof window !== 'undefined' ? window.CribbageScorer : null) || CribbageScorer;
          const optimal = (Scorer && typeof Scorer.findOptimalTwistedSplit === 'function')
            ? Scorer.findOptimalTwistedSplit(combined, starterArg, settings)
            : { hand1: combined.slice(0, 4), hand2: combined.slice(4, 8) };
          resolveTwistedCribbageSplitFn(optimal.hand1, optimal.hand2, i, context);
        }, 1100);
        return;
      }
    }

    state.showAct = key;
    state.showRun = 0;
    if (key === 'crib') {
      state.revealCrib = true;
      resetAnims();
    }
    const pack = state.lastShow[key];
    const isShowingHuman = isSeatHuman(pack.who);

    if (key === 'crib' && settings.optShotgunCribbage) {
      state.showMeter = { act: pack.title, total: 0, line: 'Shotgun Cribbage: Dealer does not count crib points' };
      ShowModal.renderShowCountModal(pack, i, context);
      setShowMeter(pack.title, 0, 'Shotgun Cribbage: Dealer does not count crib points');
      fancyCall('Shotgun: Crib Not Counted', 'go', 1200);
      render();
      netPush({ fancy: { text: 'Shotgun: Crib Not Counted', kind: 'go', ms: 1200 } });
      setTimeout(() => {
        if (state && state.over) return;
        finishShowAct(i, context);
      }, 1200);
      return;
    }

    const isMuggins = Boolean(settings.optMuggins || settings.showDetail === 'muggins');
    if (isMuggins && isShowingHuman && !pack.mugginsCountResolved) {
      render();
      ShowModal.openMugginsCountPrompt(key, pack, i, context);
      return;
    }

    state.showMeter = { act: pack.title, total: 0, line: 'Flipping…' };
    ShowModal.renderShowCountModal(pack, i, context);
    setShowMeter(pack.title, 0, 'Flipping…');
    const actCallout = key === 'crib' ? 'The Crib' : (pack.title || (key === 'pone' ? 'Pone’s Hand' : 'Dealer’s Hand'));
    fancyCall(actCallout, '', 700);
    render();
    netPush({ fancy: { text: actCallout, kind: '', ms: 700 } });
    const introDelay = (i === 0) ? 1200 : 700;
    if ((settings.showDetail || 'highlight') === 'totals') {
      setTimeout(() => {
        if (state && state.over) return;
        finishShowAct(i, context);
      }, (i === 0) ? 900 : 500);
    } else {
      setTimeout(() => {
        if (state && state.over) return;
        tickShowLine(i, 0, context);
      }, introDelay);
    }
  }

  /**
   * Completes a show act, evaluates zone blocks, serves penalties, and advances to next act.
   * @param {number} actIdx
   * @param {Object} [context]
   */
  static finishShowAct(actIdx, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'show') return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const scoreKey = context.scoreKey || (typeof window !== 'undefined' ? window.scoreKey : (s) => s);
    const isInPegThroughZone = context.isInPegThroughZone || (typeof window !== 'undefined' ? window.isInPegThroughZone : () => false);
    const isInLast5PegOut = context.isInLast5PegOut || (typeof window !== 'undefined' ? window.isInLast5PegOut : () => false);
    const isInBunker = context.isInBunker || (typeof window !== 'undefined' ? window.isInBunker : () => false);
    const applyHighlights = context.applyHighlights || (typeof window !== 'undefined' ? window.applyHighlights : () => {});
    const setShowMeter = context.setShowMeter || (typeof window !== 'undefined' ? window.setShowMeter : ShowModal.setShowMeter);
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : () => {});
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const famousFanfare = context.famousFanfare || (typeof window !== 'undefined' ? window.famousFanfare : ShowModal.famousFanfare);
    const addScore = context.addScore || (typeof window !== 'undefined' ? window.addScore : () => {});
    const openShowModal = context.openShowModal || (typeof window !== 'undefined' ? window.openShowModal : ShowModal.open);
    const beginShowAct = context.beginShowAct || (typeof window !== 'undefined' ? window.beginShowAct : ShowModal.beginShowAct);

    const key = state.showQueue[actIdx];
    const pack = state.lastShow[key];
    const currentLeadPeg = (state.scores && state.scores[scoreKey(pack.who)]) || 0;
    const inPegZone = settings.optPegThroughZones && isInPegThroughZone(currentLeadPeg);
    const inLast5 = settings.optLast5PegOut && isInLast5PegOut(currentLeadPeg);
    const inBunker = settings.optBunkers && isInBunker(currentLeadPeg);
    const isBlocked = inPegZone || inLast5 || inBunker;
    pack.blockedByZone = isBlocked;
    pack.blockedReason = inBunker ? 'Bunker' : (inLast5 ? 'Last 5 Peg Out' : (inPegZone ? 'Peg Zone' : ''));
    pack.leadPegWhenScored = currentLeadPeg;
    const total = isBlocked ? 0 : pack.signed;
    state.highlightIds = [];
    applyHighlights([]);

    if (isBlocked) {
      const t = Number(settings.target) || 121;
      let zoneHoles = '';
      if (inBunker) {
        if (currentLeadPeg >= 1 && currentLeadPeg <= 5) zoneHoles = '1–5';
        else if (currentLeadPeg >= 50 && currentLeadPeg <= 60) zoneHoles = '50–60';
        else if (currentLeadPeg >= 80 && currentLeadPeg <= 90) zoneHoles = '80–90';
        else zoneHoles = `${t - 5}–${t - 1}`;
      } else if (inLast5) {
        zoneHoles = `${t - 5}–${t - 1}`;
      } else {
        zoneHoles = (currentLeadPeg <= 40) ? '31–40' : (currentLeadPeg <= 70 ? '61–70' : '91–100');
      }
      const reasonLabel = pack.blockedReason;
      const note = `Hole ${currentLeadPeg} in ${reasonLabel} (${zoneHoles}) — 0 pts`;
      state.showMeter = { act: pack.title, total: 0, line: note };
      setShowMeter(pack.title, 0, note);
      log(`${pName(pack.who)} lead peg at hole ${currentLeadPeg} in ${reasonLabel} (${zoneHoles}) — ${key === 'crib' ? 'crib' : 'hand'} (${pack.signed} pts) not counted.`);
      fancyCall(`${reasonLabel}: 0 pts`, 'go', 1200);
      renderScores(true);
      netPush({
        fancy: { text: `${reasonLabel} (${currentLeadPeg}) — 0 pts`, kind: 'go', ms: 1200 },
        sfx: 'bell31'
      });
    } else {
      // Muggins check: if CPU shows with optMuggins enabled, occasionally undercount
      const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : (s => s === 'p1'));
      const mySeat = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : 'p1') || 'p1';
      const isShowingHuman = isSeatHuman(pack.who);

      const isMuggins = Boolean(settings.optMuggins || settings.showDetail === 'muggins');

      if (isMuggins && !isShowingHuman && total >= 3) {
        const profile = settings.aiProfile || 'shark';
        const missProb = profile === 'gambler' ? 0.22 : (profile === 'turtler' ? 0.12 : 0.05);
        if (Math.random() < missProb) {
          const missedPts = Math.min(total, (Math.random() < 0.65 ? 2 : (total >= 6 ? 4 : 2)));
          const declaredTotal = total - missedPts;
          state.showMeter = { act: pack.title, total: declaredTotal, line: `Declared ${declaredTotal} pts` };
          setShowMeter(pack.title, declaredTotal, `Declared ${declaredTotal} pts`);
          addScore(pack.who, declaredTotal, (key === 'crib' ? 'crib: ' : 'hand: ') + (pack.score.notes.join(', ') || '0') + ` (missed ${missedPts} pts)`);
          renderScores(true);

          ShowModal.triggerMugginsPrompt({
            who: pack.who,
            claimant: mySeat,
            missed: missedPts,
            declared: declaredTotal,
            total,
            actIdx,
            context
          });
          return;
        }
      }

      if (isMuggins && isShowingHuman && pack.claimedPoints !== undefined) {
        const claimed = Number(pack.claimedPoints) || 0;
        const actual = total;

        if (claimed < actual) {
          const missedPts = actual - claimed;
          state.showMeter = { act: pack.title, total: claimed, line: `Claimed ${claimed} pts (${missedPts} uncounted)` };
          setShowMeter(pack.title, claimed, `Claimed ${claimed} pts (${missedPts} uncounted)`);
          addScore(pack.who, claimed, (key === 'crib' ? 'crib: ' : 'hand: ') + (pack.score.notes.join(', ') || '0') + ` (claimed ${claimed}, missed ${missedPts} pts)`);
          log(`⚠️ ${pName(pack.who)} claimed ${claimed} pts but held ${actual} pts (${missedPts} uncounted points)!`);

          const opponentSeat = pack.who === 'p1' ? 'p2' : 'p1';
          const isOpponentHuman = isSeatHuman(opponentSeat);

          if (isOpponentHuman) {
            renderScores(true);
            ShowModal.triggerMugginsPrompt({
              who: pack.who,
              claimant: opponentSeat,
              missed: missedPts,
              declared: claimed,
              total: actual,
              actIdx,
              context
            });
            return;
          } else {
            // Opponent is AI! AI evaluates whether it catches the human's undercount
            const profile = settings.aiProfile || 'shark';
            let catchProb = 0.85;
            if (['expert', 'shark', 'master'].includes(profile)) {
              catchProb = 0.90;
            } else if (['advanced', 'turtler', 'maverick'].includes(profile)) {
              catchProb = 0.75;
            } else if (['intermediate', 'gambler', 'regular'].includes(profile)) {
              catchProb = 0.55;
            } else if (['beginner', 'rookie', 'casual'].includes(profile)) {
              catchProb = 0.35;
            }
            if (Math.random() < catchProb) {
              setTimeout(() => {
                log(`⚡ MUGGINS! ${pName(opponentSeat)} noticed your undercount and stole ${missedPts} points!`);
                fancyCall(`MUGGINS! ${pName(opponentSeat)} STOLE ${missedPts} PTS!`, 'muggins-steal', 2600);
                addScore(opponentSeat, missedPts, `Muggins steal from ${pName(pack.who)}`);
                renderScores(true);
              }, 1000);
            } else {
              setTimeout(() => {
                log(`⏳ ${pName(opponentSeat)} failed to notice the undercount! The ${missedPts} uncounted points are lost.`);
                fancyCall(`Undercount missed! (${missedPts} pts lost)`, '', 2000);
              }, 1000);
            }
          }
        } else if (claimed > actual) {
          log(`⚠️ OVERCOUNT FOUL: ${pName(pack.who)} claimed ${claimed} pts but only holds ${actual} pts. Score capped at ${actual} pts.`);
          fancyCall(`Overcount! Capped at ${actual} pts`, 'go', 1800);
          state.showMeter = { act: pack.title, total: actual, line: `Overcount: ${actual} pts max` };
          setShowMeter(pack.title, actual, `Overcount: ${actual} pts max`);
          famousFanfare(Math.abs(actual));
          addScore(pack.who, actual, (key === 'crib' ? 'crib: ' : 'hand: ') + (pack.score.notes.join(', ') || '0') + ` (overcount claimed ${claimed})`);
        } else {
          log(`⭐ PERFECT COUNT: ${pName(pack.who)} accurately counted all ${actual} pts!`);
          fancyCall(`Perfect Count! ${actual} pts`, 'combo', 1800);
          state.showMeter = { act: pack.title, total: actual, line: actual ? 'Perfect Total' : 'Zero points' };
          setShowMeter(pack.title, actual, actual ? 'Perfect Total' : 'Zero points');
          famousFanfare(Math.abs(actual));
          addScore(pack.who, actual, (key === 'crib' ? 'crib: ' : 'hand: ') + (pack.score.notes.join(', ') || '0'));
        }
      } else {
        state.showMeter = { act: pack.title, total, line: total ? 'Total' : 'No points' };
        setShowMeter(pack.title, total, total ? 'Total' : 'No points');
        famousFanfare(Math.abs(pack.score.total));
        addScore(pack.who, total, (key === 'crib' ? 'crib: ' : 'hand: ') + (pack.score.notes.join(', ') || '0'));
      }
      if (typeof document !== 'undefined') {
        const feed = document.getElementById('showComboFeed');
        if (feed) {
          const tot = document.createElement('div');
          tot.className = 'show-combo-item total';
          tot.innerHTML = `<span><strong>Total</strong></span><span class="combo-pts">${total} pts</span>`;
          feed.appendChild(tot);
          feed.scrollTop = feed.scrollHeight;
        }
      }
      if (settings.optJail && state.jailed && state.jailed[scoreKey(pack.who)] && total === 0) {
        const sk = scoreKey(pack.who);
        state.jailed[sk].turnsRemaining--;
        state.jailed[sk].step++;
        const left = state.jailed[sk].turnsRemaining;
        const used = 3 - left;
        log(`🔒 JAILED: ${pName(pack.who)} counted hand (0 pts) — Turn ${used} of 3 used (${left} remaining).`);
        fancyCall(`MISSED TURN (${used} OF 3 USED)`, 'jail', 1400);
        if (left === 0) {
          log(`🔓 FREEDOM: ${pName(pack.who)} served 3 of 3 missed turns and returns to the pegging track at hole ${state.jailed[sk].jailHole}!`);
          delete state.jailed[sk];
          fancyCall('RELEASED FROM JAIL!', 'jail-free', 1400);
        }
        renderScores(true);
      }
      if (settings.optBar && state.atBar && state.atBar[scoreKey(pack.who)] && total === 0) {
        const sk = scoreKey(pack.who);
        state.atBar[sk].turnsRemaining--;
        state.atBar[sk].step++;
        const left = state.atBar[sk].turnsRemaining;
        const used = 3 - left;
        log(`🍻 AT THE BAR: ${pName(pack.who)} counted hand (0 pts) — You lost a turn, take a drink — Turn ${used} of 3 used (${left} remaining).`);
        fancyCall(`TAKE A DRINK (${used} OF 3 USED)`, 'bar', 1500);
        if (left === 0) {
          log(`🍺 TAB CLOSED: ${pName(pack.who)} served 3 of 3 missed turns and leaves The Bar, returning to the pegging track at hole ${state.atBar[sk].barHole}!`);
          delete state.atBar[sk];
          fancyCall('LEAVING THE BAR!', 'bar-free', 1400);
        }
        renderScores(true);
      }
      if (settings.optTimeout && state.inTimeout && state.inTimeout[scoreKey(pack.who)] && total === 0) {
        const sk = scoreKey(pack.who);
        state.inTimeout[sk].turnsRemaining--;
        state.inTimeout[sk].step++;
        const left = state.inTimeout[sk].turnsRemaining;
        const used = 3 - left;
        log(`⏳ TIMEOUT: ${pName(pack.who)} counted hand (0 pts) — You've been bad, you lose a turn — Turn ${used} of 3 used (${left} remaining).`);
        fancyCall(`IN TIMEOUT (${used} OF 3 USED)`, 'timeout', 1500);
        if (left === 0) {
          log(`✨ TIMEOUT OVER: ${pName(pack.who)} served 3 of 3 missed turns and leaves timeout, returning to the pegging track at hole ${state.inTimeout[sk].timeoutHole}!`);
          delete state.inTimeout[sk];
          fancyCall('OUT OF TIMEOUT!', 'timeout-free', 1400);
        }
        renderScores(true);
      }
      renderScores(true);
      netPush({
        sfx: Math.abs(pack.score.total) >= 20 ? 'fanfare20' : (total ? 'peg' : null),
        fancy: total ? { text: total + ' Points!', kind: 'famous', ms: 1000 } : null
      });

      try {
        const repMgr = (typeof window !== 'undefined' && window.CrazyCribbage?.GameReplayManager) || GameReplayManager || (typeof window !== 'undefined' ? window.GameReplayManager : null);
        if (repMgr && repMgr.currentReplay && !pack._replayRecorded) {
          pack._replayRecorded = true;
          const isCrib = key === 'crib';
          const whoKey = (pack.who === 'you' || pack.who === 'p1') ? 'p1' : (pack.who === 'cpu' || pack.who === 'p2' ? 'p2' : pack.who);
          const p1Score = state.scores ? (state.scores.you ?? state.scores.p1 ?? 0) : 0;
          const p2Score = state.scores ? (state.scores.cpu ?? state.scores.p2 ?? 0) : 0;
          const comboList = pack.score?.lines || pack.score?.items || pack.score?.notes || [];

          repMgr.recordEvent(isCrib ? 'show_crib' : 'show_hand', {
            round: (state.handsCompleted || 0) + 1,
            who: whoKey,
            starter: state.starter,
            cards: pack.cards ? [...pack.cards] : (isCrib && state.crib ? [...state.crib] : []),
            points: total,
            combos: Array.isArray(comboList) ? [...comboList] : [],
            scores: { p1: p1Score, p2: p2Score },
            narration: `⭐ ${pName(pack.who)} scored ${total} pts in ${isCrib ? 'crib' : 'hand'}`
          });
        }
      } catch (_) {}
    }
    const myKey = scoreKey(pack.who);
    const newScore = (state.scores && state.scores[myKey]) || 0;
    const getActiveTracks = context.getActiveTracks || (typeof window !== 'undefined' ? window.getActiveTracks : () => ['p1', 'p2']);
    const activeTracks = getActiveTracks();
    const passedPlayers = [];
    for (const track of activeTracks) {
      if (track === myKey) continue;
      const otherScore = (state.scores && state.scores[track]) || 0;
      if (currentLeadPeg <= otherScore && newScore > otherScore && (currentLeadPeg < otherScore || otherScore > 0)) {
        passedPlayers.push({
          track,
          name: pName(track),
          score: otherScore
        });
      }
    }

    const handleGameOverCleanup = () => {
      state.showModal = null;
      state.showMeter = null;
      if (typeof setShowMeter === 'function') setShowMeter('', 0, '');
      if (typeof document !== 'undefined') {
        const m = document.getElementById('showMeter');
        if (m) m.classList.add('hidden');
      }
      const checkGameOverFn = context.checkGameOver || (typeof window !== 'undefined' ? window.checkGameOver : null);
      if (typeof checkGameOverFn === 'function') checkGameOverFn(context);
    };

    if (state.over) {
      handleGameOverCleanup();
      return;
    }

    const continueShow = () => {
      if (state.over) {
        handleGameOverCleanup();
        return;
      }
      if (state.pausedForSign || state.pausedForAdultTimer || state.pausedForAdultStrip || state.pausedForPintsBonus || state.paused) {
        const resumeCb = () => {
          if (state.over) {
            handleGameOverCleanup();
            return;
          }
          setTimeout(() => beginShowAct(actIdx + 1, context), 700);
        };
        state._resumeAfterSign = resumeCb;
        state._resumeAfterAdultTimer = resumeCb;
        state._resumeAfterPintsBonus = resumeCb;
        return;
      }
      setTimeout(() => beginShowAct(actIdx + 1, context), 700);
    };

    if (passedPlayers.length > 0) {
      // Sort passed players descending by score so firstPassed is the previous leader who got overtaken
      passedPlayers.sort((a, b) => (b.score || 0) - (a.score || 0));
      const firstPassed = passedPlayers[0];

      // The passer is the player who is now in the lead; weapon card is the last card in their hand
      const passerCards = (pack.cards && pack.cards.length) ? pack.cards : ShowModal.getPlayerHandCards(pack.who, state, context);
      const passerCard = (passerCards && passerCards.length) ? passerCards[passerCards.length - 1] : null;

      // The passed player is no longer in the lead; killed card is the last card in their hand
      const passedCards = ShowModal.getPlayerHandCards(firstPassed.track, state, context);
      const passedCard = (passedCards && passedCards.length) ? passedCards[passedCards.length - 1] : null;

      const passerName = pName(pack.who);
      const passedName = passedPlayers.map(p => p.name).join(' & ');

      const assassinOpt = (typeof settings !== 'undefined' && settings && settings.cardAssassin) || (typeof window !== 'undefined' && window.settings && window.settings.cardAssassin) || 'no';
      const isAssassinEnabled = assassinOpt === 'yes';

      const showAssassin = isAssassinEnabled ? (context.showCardAssassin || (typeof window !== 'undefined' && window.showCardAssassin) || (CardAssassinOverlay && CardAssassinOverlay.show)) : null;
      if (typeof showAssassin === 'function') {
        let finished = false;
        const safeComplete = () => {
          if (finished) return;
          finished = true;
          continueShow();
        };

        try {
          showAssassin({
            passerName,
            passedName,
            passerCard,
            passedCard,
            settings: (typeof settings !== 'undefined' ? settings : (typeof window !== 'undefined' ? window.settings : null)),
            onComplete: safeComplete
          });
          // Safety fallback timeout: if overlay ever fails or window loses focus
          setTimeout(() => {
            if (!finished) safeComplete();
          }, 10500);
          return;
        } catch (err) {
          console.error('Failed to display Card Assassin animation:', err);
          continueShow();
          return;
        }
      }
    }

    continueShow();
  }

  /**
   * Ticks and displays an individual scoring line item with highlighting.
   * @param {number} actIdx
   * @param {number} lineIdx
   * @param {Object} [context]
   */
  static tickShowLine(actIdx, lineIdx, context = {}) {
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (!state || state.phase !== 'show' || state.over) return;

    const isBonusMessage = context.isBonusMessage || (typeof window !== 'undefined' ? window.isBonusMessage : ShowModal.isBonusMessage);
    const cardKey = context.cardKey || (typeof window !== 'undefined' ? window.cardKey : (c) => c && c.id);
    const applyHighlights = context.applyHighlights || (typeof window !== 'undefined' ? window.applyHighlights : () => {});
    const setShowMeter = context.setShowMeter || (typeof window !== 'undefined' ? window.setShowMeter : ShowModal.setShowMeter);
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : { peg: () => {} });
    const netPush = context.netPush || (typeof window !== 'undefined' ? window.netPush : () => {});
    const finishShowAct = context.finishShowAct || (typeof window !== 'undefined' ? window.finishShowAct : ShowModal.finishShowAct);

    const key = state.showQueue[actIdx];
    const pack = state.lastShow[key];
    const lines = (pack.score && pack.score.lines) || [];
    if (lineIdx >= lines.length) {
      finishShowAct(actIdx, context);
      return;
    }
    const line = lines[lineIdx];
    const isBonus = Boolean(line.isBonus || isBonusMessage(line.why));
    const lineDuration = isBonus ? 2400 : 850;
    const fancyMs = isBonus ? 2200 : 750;

    state.showRun = (state.showRun || 0) + line.n;
    state.highlightIds = (line.cards || []).map(cardKey).filter(Boolean);
    applyHighlights(line.cards || [], isBonus);
    const signPrefix = line.n >= 0 ? '+' : '';
    const lineText = signPrefix + line.n + '  ' + line.why;
    state.showMeter = { act: pack.title, total: state.showRun, line: lineText, isBonus };
    setShowMeter(pack.title, state.showRun, lineText, isBonus);
    if (isBonus) {
      fancyCall(lineText, 'combo bonus-call', fancyMs);
    }
    if (sfx.peg) sfx.peg();
    netPush({
      fancy: { text: lineText, kind: isBonus ? 'combo bonus-call' : 'combo', ms: fancyMs },
      sfx: 'peg'
    });

    if (typeof document !== 'undefined') {
      const feed = document.getElementById('showComboFeed');
      if (feed) {
        const item = document.createElement('div');
        item.className = 'show-combo-item' + (isBonus ? ' bonus' : '');
        item.innerHTML = `<span>${line.why}</span><span class="combo-pts">${line.n >= 0 ? '+' : ''}${line.n}</span>`;
        feed.appendChild(item);
        feed.scrollTop = feed.scrollHeight;
      }
    }

    setTimeout(() => {
      if (state && state.over) return;
      ShowModal.tickShowLine(actIdx, lineIdx + 1, context);
    }, lineDuration);
  }

  /**
   * Triggers the Muggins action banner and countdown timer when a player undercounts.
   * @param {Object} info
   */
  static triggerMugginsPrompt(info) {
    const { who, claimant, missed, declared, total, actIdx, context } = info;
    const pName = context.pName || (typeof window !== 'undefined' ? window.pName : (s) => s);
    const fancyCall = context.fancyCall || (typeof window !== 'undefined' ? window.fancyCall : () => {});
    const addScore = context.addScore || (typeof window !== 'undefined' ? window.addScore : () => {});
    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : () => {});
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const famousFanfare = context.famousFanfare || (typeof window !== 'undefined' ? window.famousFanfare : ShowModal.famousFanfare);
    const beginShowAct = context.beginShowAct || (typeof window !== 'undefined' ? window.beginShowAct : ShowModal.beginShowAct);

    fancyCall(`${pName(who)} declared ${declared} pts!`, 'go', 1200);
    log(`⚠️ MUGGINS ALERT: ${pName(who)} counted ${declared} pts but held ${total} pts (${missed} pts uncounted)!`);

    const wrap = typeof document !== 'undefined' ? document.getElementById('mugginsPromptWrap') : null;
    const btn = typeof document !== 'undefined' ? document.getElementById('btnMugginsClaim') : null;
    const text = typeof document !== 'undefined' ? document.getElementById('mugginsText') : null;
    const timerEl = typeof document !== 'undefined' ? document.getElementById('mugginsTimer') : null;

    let timeLeft = 5;
    let timerId = null;
    let resolved = false;

    const cleanup = () => {
      if (timerId) clearInterval(timerId);
      if (wrap) wrap.classList.add('hidden');
      if (typeof window !== 'undefined') window.claimMuggins = null;
    };

    const onClaim = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      log(`⚡ MUGGINS! ${pName(claimant)} caught ${pName(who)}'s undercount and stole ${missed} points!`);
      fancyCall(`MUGGINS! +${missed} PTS STOLEN!`, 'muggins-steal', 2000);
      if (claimant === 'p1' || claimant === 'you') {
        const sfx = (typeof window !== 'undefined' ? window.sfx : null);
        AchievementManager.incrementStat('mugginsCalls', 1, { sfx });
        AchievementManager.unlock('cutthroat_king', { sfx });
      }
      famousFanfare(missed);
      addScore(claimant, missed, `Muggins steal from ${pName(who)}`);
      renderScores(true);
      setTimeout(() => beginShowAct(actIdx + 1, context), 900);
    };

    if (typeof window !== 'undefined') {
      window.claimMuggins = onClaim;
    }

    if (wrap && btn) {
      if (text) text.textContent = `${pName(who)} counted ${declared} pts (missed ${missed} pts). Tap MUGGINS to steal!`;
      if (timerEl) timerEl.textContent = `${timeLeft}s`;
      wrap.classList.remove('hidden');
      btn.addEventListener('click', onClaim);

      timerId = setInterval(() => {
        timeLeft--;
        if (timerEl) timerEl.textContent = `${timeLeft}s`;
        if (timeLeft <= 0) {
          if (resolved) return;
          resolved = true;
          cleanup();
          log(`⏳ Muggins timer expired — ${missed} uncounted points lost.`);
          setTimeout(() => beginShowAct(actIdx + 1, context), 500);
        }
      }, 1000);
    } else {
      setTimeout(() => beginShowAct(actIdx + 1, context), 700);
    }
  }

  /**
   * Saves stripped match round summary snapshot into gameHands array.
   * @param {Object} state
   * @param {Array} gameHands
   */
  static saveHand(state, gameHands = []) {
    if (!state || !Array.isArray(gameHands)) return;
    const clonedState = { ...state };
    const keysToDelete = [
      "assignTarget",
      "atBar",
      "count",
      "cpuHand",
      "currentHumanDiscard",
      "customStatus",
      "cutDeck",
      "cutPicks",
      "deck",
      "deckDiscards",
      "discardsComplete",
      "frozen",
      "goPending",
      "highlightIds",
      "inDetour",
      "inRoundAbout",
      "inTimeout",
      "jailed",
      "over",
      "pausedForSign",
      "pausedForAdultTimer",
      "pausedForAdultStrip",
      "pausedForPintsBonus",
      "paused",
      "pendingSuit",
      "pendingWild",
      "phase",
      "recorded",
      "revealCrib",
      "selected",
      "showAct",
      "showMeter",
      "showModal",
      "showRun",
      "signsState",
      "starterFace",
      "stolenCrib",
      "stolenCribResolved",
      "twistedCribbageResolved",
      "twisted8Cards",
      "originalDealerKept",
      "originalCrib",
      "turn",
      "youHand",
      "_resumeAfterSign",
      "_resumeAfterAdultTimer",
      "_resumeAfterPintsBonus",
    ];
    keysToDelete.forEach(key => delete clonedState[key]);
    gameHands.push(clonedState);
  }
}
